import { useEffect, useState, useCallback } from 'react'
import { format, startOfWeek, addDays, differenceInMinutes, parse } from 'date-fns'
import { fr } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, Plus, X, Send, Trash2, Sun, Moon, Copy, Printer, Calendar, LayoutList, BookmarkPlus, BookOpen, AlertTriangle, Euro, Zap, Wand2, Cloud, Upload, Download, TrendingUp, Sparkles, Eraser, GripVertical, ArrowUp, ArrowDown } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'
import { useModules } from '../lib/modules'
import ImportPlanningExcel from '../components/ImportPlanningExcel'
import { useIsPhone } from '../lib/useIsPhone'
import { sortByExcelOrder } from '../lib/excelOrder'

interface Employee { id: string; firstName: string; lastName: string; weeklyHours: number; hourlyRate: number; employerCostRate: number; rowColor?: string | null; contractType?: string }
interface Position { id: string; name: string; color: string }
interface Shift {
  id: string; employeeId: string; positionId: string | null; date: string
  startTime: string; endTime: string; breakMinutes: number; note: string | null
  status: string; position: Position | null; sourceGeneration?: string | null
  // Si rempli, ce shift est une absence approuvée (CP/MAL/…)
  absenceId?: string | null
  absence?: { id: string; absenceType: { name: string; code: string; color: string; isPaid: boolean } } | null
}
interface Absence {
  id: string; employeeId: string; startDate: string; endDate: string
  absenceType: { name: string; code: string; color: string }
}
type HalfDay = 'morning' | 'afternoon'
interface ModalData { employeeId: string; date: string; halfDay: HalfDay; shift?: Shift }

interface OpeningDay { closed: boolean; open: string; close: string }
interface DeliveryPeriod { id: string; label: string; startMonth: number; startDay: number; endMonth: number; endDay: number; days: number[] }
interface BusinessRules { openingHours: OpeningDay[]; deliveryPeriods: DeliveryPeriod[] }

interface WeatherEntry { icon: string; label: string; tmax: number | null; tmin: number | null }
interface WeatherDay {
  date: string
  forecast: WeatherEntry | null
  n1: { date: string; weather: WeatherEntry | null; revenue: number | null }
}

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

function shiftDuration(shift: Shift): number {
  const start = parse(shift.startTime, 'HH:mm', new Date())
  const end = parse(shift.endTime, 'HH:mm', new Date())
  let mins = differenceInMinutes(end, start)
  if (mins < 0) mins += 24 * 60
  return Math.max(0, mins / 60)
}

function weekHours(shifts: Shift[], empId: string): number {
  return shifts.filter(s => s.employeeId === empId).reduce((sum, s) => sum + shiftDuration(s), 0)
}

function isMorning(shift: Shift): boolean {
  return shift.startTime < '12:00'
}

function shiftNetHours(shift: Shift): number {
  const [sh, sm] = shift.startTime.split(':').map(Number)
  const [eh, em] = shift.endTime.split(':').map(Number)
  let mins = (eh * 60 + em) - (sh * 60 + sm)
  if (mins < 0) mins += 24 * 60
  return Math.max(0, (mins - shift.breakMinutes) / 60)
}

function dayNetHours(shifts: Shift[], empId: string, dateStr: string): number {
  return shifts.filter(s => s.employeeId === empId && s.date === dateStr).reduce((sum, s) => sum + shiftNetHours(s), 0)
}

function weeklyEmployerCost(shifts: Shift[], emp: Employee): number {
  const hours = weekHours(shifts, emp.id)
  return hours * emp.hourlyRate * (1 + (emp.employerCostRate || 45) / 100)
}

function totalWeeklyCost(shifts: Shift[], employees: Employee[]): number {
  return employees.reduce((sum, emp) => sum + weeklyEmployerCost(shifts, emp), 0)
}

const GRID = '160px repeat(14, minmax(58px, 1fr)) 70px'
// Trait noir entre chaque jour (et avant lundi) dans la vue semaine
const DAY_SEP = '!border-r-2 !border-r-gray-900'

export default function Planning() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }))
  const [viewModeChoice, setViewMode] = useState<'week' | 'day'>('week')
  // Sur téléphone : toujours la vue « jour » (la grille de la semaine est illisible) ;
  // les boutons d'action sont repliés derrière « Actions »
  const isPhone = useIsPhone()
  const viewMode = isPhone ? 'day' : viewModeChoice
  const [showActions, setShowActions] = useState(false)
  const [selectedDay, setSelectedDay] = useState<Date>(() => new Date())
  const [employees, setEmployees] = useState<Employee[]>([])
  const [positions, setPositions] = useState<Position[]>([])
  const [shifts, setShifts] = useState<Shift[]>([])
  const [absences, setAbsences] = useState<Absence[]>([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [duplicating, setDuplicating] = useState(false)
  const [syncing, setSyncing] = useState<'pull' | 'push' | null>(null)
  const [modal, setModal] = useState<ModalData | null>(null)
  const [saving, setSaving] = useState(false)
  const [filterPosition, setFilterPosition] = useState('')
  const [filterEmployee, setFilterEmployee] = useState('')
  const [templates, setTemplates] = useState<any[]>([])
  const [saveTemplateModal, setSaveTemplateModal] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [applyingTemplate, setApplyingTemplate] = useState(false)
  const [smartPlannerOpen, setSmartPlannerOpen] = useState(false)
  const [copiedShift, setCopiedShift] = useState<Shift | null>(null)
  const [weather, setWeather] = useState<WeatherDay[] | null>(null)
  const [affluence, setAffluence] = useState<{ date: string; factor: number | null; caN1: number | null }[] | null>(null)
  const [showRevenueImport, setShowRevenueImport] = useState(false)
  const [showOrderModal, setShowOrderModal] = useState(false)
  const [employeeOrder, setEmployeeOrder] = useState<string[]>([])
  const { current: establishment } = useEstablishment()
  const { modules } = useModules()
  const [showExcelImport, setShowExcelImport] = useState(false)
  const [lastImport, setLastImport] = useState<{ id: string; fileName: string; dateFrom: string; dateTo: string; shiftsCreated: number; createdAt: string; employeeOrder?: string[] } | null>(null)

  const loadLastImport = useCallback(() => {
    if (!establishment) return
    api.get(`/planning-import/last?establishmentId=${establishment.id}`)
      .then(r => setLastImport(r.data)).catch(() => setLastImport(null))
  }, [establishment])
  useEffect(() => { loadLastImport() }, [loadLastImport])

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  const load = useCallback(async () => {
    if (!establishment) return
    setLoading(true)
    try {
      const estabParam = `&establishmentId=${establishment.id}`
      const from = format(weekStart, 'yyyy-MM-dd')
      const to   = format(addDays(weekStart, 6), 'yyyy-MM-dd')
      const [empRes, posRes, shiftRes, absRes] = await Promise.all([
        api.get(`/employees?status=active${estabParam}`),
        api.get('/shifts/positions'),
        api.get(`/shifts?weekStart=${from}${estabParam}`),
        // Module congés éteint : pas d'appel (l'API répondrait 403)
        modules.conges
          ? api.get(`/absences?from=${from}&to=${to}&status=approved`)
          : Promise.resolve({ data: [] })
      ])
      setEmployees(empRes.data)
      setPositions(posRes.data)
      setShifts(shiftRes.data)
      setAbsences(absRes.data)
    } finally {
      setLoading(false)
    }
  }, [weekStart, establishment, modules.conges])

  useEffect(() => { load() }, [load])

  // ── Sync Google Sheets : boutons manuels ────────────────────────────────
  async function syncPullFromSheets() {
    if (!establishment) return
    if (!confirm('Importer les horaires saisis dans Google Sheets vers cette app ? Les shifts précédemment importés depuis Sheets seront remplacés.')) return
    setSyncing('pull')
    try {
      const r = await api.post('/sheets/pull', { establishmentId: establishment.id, year: new Date().getFullYear() })
      alert(`Import OK : ${r.data.shiftsCreated} shifts créés, ${r.data.shiftsDeleted} anciens supprimés.`)
      await load()
    } catch (e: any) {
      alert('Erreur import : ' + (e?.response?.data?.error || e.message))
    } finally {
      setSyncing(null)
    }
  }
  async function syncPushToSheets() {
    if (!establishment) return
    setSyncing('push')
    try {
      const r = await api.post('/sheets/push', { establishmentId: establishment.id })
      alert(`Push OK : ${r.data.shiftsWritten || 0} horaires + ${r.data.absencesWritten || 0} absences écrits du ${r.data.from} au ${r.data.to}.`)
    } catch (e: any) {
      alert('Erreur push : ' + (e?.response?.data?.error || e.message))
    } finally {
      setSyncing(null)
    }
  }

  // Charger les modèles de planning
  useEffect(() => {
    if (!establishment) return
    api.get('/shifts/templates').then(r => setTemplates(r.data)).catch(() => {})
  }, [establishment])

  // Bandeaux Affluence / Météo / Stats : outils du planning automatique,
  // masqués quand ce module est débranché (planning fait dans Excel)
  // Charger la météo
  useEffect(() => {
    if (!establishment || !modules.planningAuto) { setWeather(null); return }
    const from = format(weekStart, 'yyyy-MM-dd')
    api.get(`/weather?weekStart=${from}&establishmentId=${establishment.id}`)
      .then(r => setWeather(r.data.hasCoordinates ? r.data.days : null))
      .catch(() => setWeather(null))
  }, [weekStart, establishment, modules.planningAuto])

  // Charger l'affluence IA (facteur CA N-1 par jour — même moteur que la génération)
  useEffect(() => {
    if (!establishment || !modules.planningAuto) { setAffluence(null); return }
    const from = format(weekStart, 'yyyy-MM-dd')
    api.get(`/shifts/affluence?weekStart=${from}&establishmentId=${establishment.id}`)
      .then(r => setAffluence(r.data.days))
      .catch(() => setAffluence(null))
  }, [weekStart, establishment, modules.planningAuto])

  async function saveAsTemplate() {
    if (!establishment || !templateName.trim()) return
    setSavingTemplate(true)
    try {
      const res = await api.post('/shifts/templates', {
        name: templateName.trim(),
        establishmentId: establishment.id,
        weekStart: format(weekStart, 'yyyy-MM-dd')
      })
      setTemplates(prev => [res.data, ...prev])
      setSaveTemplateModal(false)
      setTemplateName('')
    } finally {
      setSavingTemplate(false)
    }
  }

  async function applyTemplate(templateId: string) {
    if (!establishment) return
    setApplyingTemplate(true)
    try {
      await api.post(`/shifts/templates/${templateId}/apply`, {
        establishmentId: establishment.id,
        weekStart: format(weekStart, 'yyyy-MM-dd')
      })
      await load()
    } finally {
      setApplyingTemplate(false)
    }
  }

  async function deleteTemplate(id: string) {
    await api.delete(`/shifts/templates/${id}`)
    setTemplates(prev => prev.filter(t => t.id !== id))
  }

  // Sync selectedDay to current week when switching weeks
  useEffect(() => {
    if (viewMode === 'day') {
      const dayInWeek = days.find(d => format(d, 'yyyy-MM-dd') === format(selectedDay, 'yyyy-MM-dd'))
      if (!dayInWeek) setSelectedDay(weekStart)
    }
  }, [weekStart])

  function getAbsence(empId: string, dateStr: string): Absence | null {
    return absences.find(a =>
      a.employeeId === empId && a.startDate <= dateStr && a.endDate >= dateStr
    ) || null
  }

  async function handleSmartGenerate(params: any) {
    if (!establishment) return null
    const res = await api.post('/shifts/smart-generate', {
      establishmentId: establishment.id,
      weekStart: format(weekStart, 'yyyy-MM-dd'),
      params
    })
    await load()
    return res.data
  }

  async function handleCycleGenerate(cycleStart: string, params: any) {
    if (!establishment) return null
    const res = await api.post('/shifts/cycle-generate', {
      establishmentId: establishment.id,
      cycleStart,
      params
    })
    await load()
    return res.data
  }

  async function duplicateWeek() {
    if (!establishment) return
    setDuplicating(true)
    try {
      const targetWeekStart = format(addDays(weekStart, 7), 'yyyy-MM-dd')
      await api.post('/shifts/duplicate-week', {
        establishmentId: establishment.id,
        sourceWeekStart: format(weekStart, 'yyyy-MM-dd'),
        targetWeekStart
      })
      // Navigate to next week after duplication
      setWeekStart(prev => addDays(prev, 7))
    } finally {
      setDuplicating(false)
    }
  }

  async function clearWeek() {
    if (!establishment) return
    if (!window.confirm('Supprimer tous les shifts en brouillon de cette semaine ?')) return
    await api.delete('/shifts/clear', { data: { establishmentId: establishment.id, weekStart: format(weekStart, 'yyyy-MM-dd') } })
    load()
  }

  async function clearAll() {
    if (!establishment) return
    if (!window.confirm('⚠️ Supprimer TOUS les shifts (toutes semaines, brouillons ET publiés) ? Cette action est irréversible.')) return
    await api.delete('/shifts/clear-all', { data: { establishmentId: establishment.id } })
    load()
  }

  function getShifts(empId: string, date: Date, half: HalfDay): Shift[] {
    const d = format(date, 'yyyy-MM-dd')
    return shifts.filter(s =>
      s.employeeId === empId &&
      s.date === d &&
      (half === 'morning' ? isMorning(s) : !isMorning(s))
    )
  }

  function openModal(employeeId: string, date: Date, half: HalfDay) {
    setModal({ employeeId, date: format(date, 'yyyy-MM-dd'), halfDay: half })
  }

  async function handlePublish() {
    if (!establishment) return
    await api.post('/shifts/publish', { establishmentId: establishment.id, weekStart: format(weekStart, 'yyyy-MM-dd') })
    load()
  }

  async function handleNotify() {
    if (!establishment) return
    const res = await api.post('/shifts/notify', { establishmentId: establishment.id, weekStart: format(weekStart, 'yyyy-MM-dd') })
    alert(`✅ ${res.data.notified} employé(s) notifié(s) du planning.`)
  }

  async function deleteShift(id: string) {
    await api.delete(`/shifts/${id}`)
    setShifts(prev => prev.filter(s => s.id !== id))
  }

  async function pasteShift(employeeId: string, date: string) {
    if (!copiedShift) return
    const res = await api.post('/shifts', {
      employeeId,
      date,
      startTime: copiedShift.startTime,
      endTime: copiedShift.endTime,
      breakMinutes: copiedShift.breakMinutes,
      positionId: copiedShift.positionId,
      note: copiedShift.note,
      establishmentId: establishment?.id,
    })
    setShifts(prev => [...prev, res.data])
  }

  const draftCount = shifts.filter(s => s.status === 'draft').length

  // Charger/sauvegarder l'ordre d'affichage des employés
  useEffect(() => {
    if (!establishment) return
    const key = `employee_order_${establishment.id}`
    const saved = localStorage.getItem(key)
    if (saved) { try { setEmployeeOrder(JSON.parse(saved)) } catch {} }
  }, [establishment])

  function saveEmployeeOrder(order: string[]) {
    if (!establishment) return
    setEmployeeOrder(order)
    localStorage.setItem(`employee_order_${establishment.id}`, JSON.stringify(order))
  }

  function moveEmployee(id: string, dir: -1 | 1) {
    const ordered = orderedEmployees.map(e => e.id)
    const idx = ordered.indexOf(id)
    if (idx === -1) return
    const newOrder = [...ordered]
    const target = idx + dir
    if (target < 0 || target >= newOrder.length) return
    ;[newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]]
    saveEmployeeOrder(newOrder)
  }

  // Filtered employees
  const filteredEmployees = employees.filter(emp => {
    if (filterEmployee && emp.id !== filterEmployee) return false
    if (filterPosition) {
      const hasPosition = shifts.some(s => s.employeeId === emp.id && s.positionId === filterPosition)
      if (!hasPosition) return false
    }
    return true
  })

  // Ordre d'affichage : celui des lignes du planning Excel quand il existe (décision Manu 27/09),
  // sinon l'ordre choisi à la main (bouton « Ordre »)
  const excelOrder = lastImport?.employeeOrder || []
  const orderedEmployees = excelOrder.length > 0
    ? sortByExcelOrder(filteredEmployees, excelOrder)
    : employeeOrder.length > 0
    ? [...filteredEmployees].sort((a, b) => {
        const ia = employeeOrder.indexOf(a.id)
        const ib = employeeOrder.indexOf(b.id)
        if (ia === -1 && ib === -1) return 0
        if (ia === -1) return 1
        if (ib === -1) return -1
        return ia - ib
      })
    : filteredEmployees

  return (
    <div className="p-3 md:p-6 md:h-full flex flex-col print:p-2">
      {/* Dernier planning Excel chargé */}
      {lastImport && (
        <div className="mb-3 flex items-center gap-2 text-xs text-gray-500 print:hidden">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          Planning Excel chargé le {new Date(lastImport.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
          {' '}({lastImport.fileName}) · du {new Date(lastImport.dateFrom + 'T12:00:00').toLocaleDateString('fr-FR')} au {new Date(lastImport.dateTo + 'T12:00:00').toLocaleDateString('fr-FR')} · {lastImport.shiftsCreated} horaires
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-3 md:mb-5 flex-shrink-0 gap-3 flex-wrap print:hidden">
        <div className="flex items-center gap-2 md:gap-3 flex-wrap w-full md:w-auto">
          <h1 className="text-xl font-bold text-gray-900">Planning</h1>

          {/* Week navigator */}
          <div className="flex items-center gap-1 bg-white border rounded-lg shadow-sm">
            <button onClick={() => setWeekStart(d => addDays(d, -7))} className="p-2 hover:bg-gray-50 rounded-l-lg transition-colors">
              <ChevronLeft size={18} />
            </button>
            <span className="px-2 md:px-4 text-sm font-medium text-gray-700 whitespace-nowrap">
              {format(weekStart, 'd MMM', { locale: fr })} – {format(addDays(weekStart, 6), 'd MMM yyyy', { locale: fr })}
            </span>
            <button onClick={() => setWeekStart(d => addDays(d, 7))} className="p-2 hover:bg-gray-50 rounded-r-lg transition-colors">
              <ChevronRight size={18} />
            </button>
          </div>
          <button
            onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            Aujourd'hui
          </button>

          {/* Téléphone : un seul bouton pour toutes les actions */}
          {isPhone && (
            <button onClick={() => setShowActions(v => !v)}
              className={`ml-auto text-sm font-medium px-3 py-1.5 rounded-lg border ${showActions ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200'}`}>
              {showActions ? 'Fermer' : 'Actions'}
            </button>
          )}

          {/* View toggle (ordinateur) */}
          <div className="hidden md:flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('week')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'week' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              <Calendar size={14} /> Semaine
            </button>
            <button
              onClick={() => setViewMode('day')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'day' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              <LayoutList size={14} /> Jour
            </button>
          </div>
        </div>

        <div className={`${isPhone && !showActions ? 'hidden' : 'flex'} items-center gap-2 flex-wrap`}>
          {/* Filters */}
          <select
            value={filterEmployee}
            onChange={e => setFilterEmployee(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Tous les employés</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
          </select>
          <select
            value={filterPosition}
            onChange={e => setFilterPosition(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Tous les postes</option>
            {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>

          {/* Planning fait main dans Excel → appli */}
          <button onClick={() => setShowExcelImport(true)}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Upload size={15} /> Charger le planning Excel
          </button>

          {/* Planning automatique + Google Sheets : masqués si le module est débranché */}
          {modules.planningAuto && <>
          <button onClick={() => setSmartPlannerOpen(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Wand2 size={15} /> Smart Planner
          </button>
          {/* Sync Google Sheets — boutons rapides */}
          <button onClick={syncPullFromSheets} disabled={syncing !== null}
            title="Importer les horaires saisis dans Google Sheets vers l'app"
            className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50">
            <Download size={14} /> {syncing === 'pull' ? 'Import...' : 'Sheets → App'}
          </button>
          <button onClick={syncPushToSheets} disabled={syncing !== null}
            title="Pousser les absences/congés validés vers Google Sheets"
            className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50">
            <Upload size={14} /> {syncing === 'push' ? 'Push...' : 'App → Sheets'}
          </button>
          </>}

          {/* Modèles */}
          <button onClick={() => { setTemplateName(''); setSaveTemplateModal(true) }}
            title="Sauvegarder cette semaine comme modèle"
            className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
            <BookmarkPlus size={14} /> Modèle
          </button>
          {templates.length > 0 && (
            <div className="relative group">
              <button
                disabled={applyingTemplate}
                className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50">
                <BookOpen size={14} /> {applyingTemplate ? 'Application...' : 'Appliquer'}
              </button>
              <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 z-30 w-64 py-1 hidden group-hover:block">
                <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">Modèles sauvegardés</p>
                {templates.map(t => (
                  <div key={t.id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 group/item">
                    <button
                      onClick={() => applyTemplate(t.id)}
                      className="flex-1 text-left">
                      <p className="text-sm font-medium text-gray-800">{t.name}</p>
                      <p className="text-xs text-gray-400">{t.shiftsCount} shift{t.shiftsCount !== 1 ? 's' : ''}</p>
                    </button>
                    <button onClick={() => deleteTemplate(t.id)}
                      className="opacity-0 group-hover/item:opacity-100 text-gray-300 hover:text-red-500 transition-all ml-2">
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ordre manuel : inutile quand l'ordre vient du planning Excel */}
          {excelOrder.length === 0 && (
          <button onClick={() => setShowOrderModal(true)}
            title="Modifier l'ordre d'affichage des employés"
            className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
            <GripVertical size={14} /> Ordre
          </button>
          )}

          <button onClick={duplicateWeek} disabled={duplicating}
            title="Dupliquer vers la semaine suivante"
            className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50">
            <Copy size={14} />
            {duplicating ? 'Copie...' : 'Dupliquer →'}
          </button>

          <button onClick={clearWeek}
            title="Supprimer tous les shifts en brouillon de cette semaine"
            className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
            <Eraser size={14} /> Vider semaine
          </button>

          <button onClick={clearAll}
            title="Supprimer TOUS les shifts de toutes les semaines"
            className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors">
            <Eraser size={14} /> Vider tout
          </button>

          <button onClick={() => window.print()}
            className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
            <Printer size={14} /> Imprimer
          </button>

          {/* Import CA N-1 : ne sert qu'à l'affluence du planning automatique */}
          {modules.planningAuto && (
          <button onClick={() => setShowRevenueImport(true)}
            title="Importer le chiffre d'affaires N-1 (CSV)"
            className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
            <Upload size={14} /> CA N-1
          </button>
          )}

          {draftCount > 0 && (
            <button onClick={handlePublish}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              <Send size={15} />
              Publier ({draftCount})
            </button>
          )}
          <button onClick={handleNotify}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Send size={15} />
            Notifier les employés
          </button>
        </div>
      </div>

      {/* Bannière shift copié */}
      {copiedShift && (
        <div className="flex items-center gap-3 mb-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700 print:hidden">
          <Copy size={14} />
          <span>Shift copié : <strong>{copiedShift.startTime}–{copiedShift.endTime}</strong>{copiedShift.position ? ` · ${copiedShift.position.name}` : ''} — clique sur une cellule vide pour coller</span>
          <button onClick={() => setCopiedShift(null)} className="ml-auto text-blue-400 hover:text-blue-600"><X size={14} /></button>
        </div>
      )}

      {/* Bandeau coûts + alertes réglementaires */}
      {!loading && employees.length > 0 && (
        <div className="flex items-center gap-4 mb-4 px-4 py-2 bg-white border border-gray-200 rounded-xl shadow-sm flex-shrink-0 flex-wrap print:hidden">
          {/* Coût salarial : donnée de paie, visible seulement avec le module RH */}
          {modules.rh && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Euro size={14} className="text-green-600" />
            <span>Coût estimé semaine :</span>
            <span className="font-bold text-gray-900">{totalWeeklyCost(shifts, employees).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}</span>
          </div>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="text-gray-400">|</span>
            <span>Heures planifiées :</span>
            <span className="font-bold text-gray-900">{shifts.reduce((sum, s) => sum + shiftNetHours(s), 0).toFixed(1)}h</span>
          </div>
          {/* Alertes réglementaires */}
          {(() => {
            const alerts: string[] = []
            employees.forEach(emp => {
              Array.from(new Set(shifts.filter(s => s.employeeId === emp.id).map(s => s.date))).forEach(date => {
                const h = dayNetHours(shifts, emp.id, date)
                if (h > 10) alerts.push(`${emp.firstName} ${emp.lastName} : ${h.toFixed(1)}h le ${date}`)
              })
              const total = weekHours(shifts, emp.id)
              if (total > emp.weeklyHours + 8) alerts.push(`${emp.firstName} ${emp.lastName} : ${total.toFixed(1)}h/sem. (limite dépassée)`)
            })
            return alerts.length > 0 ? (
              <div className="flex items-center gap-2 ml-auto">
                <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
                <span className="text-xs text-amber-700 font-medium">{alerts.length} alerte{alerts.length > 1 ? 's' : ''} réglementaire{alerts.length > 1 ? 's' : ''}</span>
                <div className="relative group">
                  <button className="text-xs text-amber-600 underline">Voir</button>
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-amber-200 z-30 w-72 py-2 hidden group-hover:block">
                    {alerts.map((a, i) => (
                      <div key={i} className="px-3 py-1.5 text-xs text-amber-800 flex items-start gap-2">
                        <AlertTriangle size={11} className="text-amber-400 mt-0.5 flex-shrink-0" />
                        {a}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null
          })()}
        </div>
      )}

      {/* Print header (only visible on print) */}
      <div className="hidden print:flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Planning — {format(weekStart, 'd MMM', { locale: fr })} au {format(addDays(weekStart, 6), 'd MMM yyyy', { locale: fr })}</h1>
        <p className="text-sm text-gray-500">Imprimé le {format(new Date(), 'd MMM yyyy', { locale: fr })}</p>
      </div>

      {/* Day selector (day view) */}
      {viewMode === 'day' && (
        <div className="flex gap-1 mb-3 md:mb-4 flex-shrink-0 print:hidden">
          {days.map((day, i) => {
            const dateStr = format(day, 'yyyy-MM-dd')
            const isSelected = dateStr === format(selectedDay, 'yyyy-MM-dd')
            const isToday = dateStr === format(new Date(), 'yyyy-MM-dd')
            return (
              <button key={i}
                onClick={() => setSelectedDay(day)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isSelected ? 'bg-blue-600 text-white' :
                  isToday ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                  'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}>
                <div className="text-xs opacity-75">{DAYS[i]}</div>
                <div className="font-bold">{format(day, 'd')}</div>
              </button>
            )
          })}
        </div>
      )}

      {/* Content */}
      <div className="md:flex-1 md:overflow-auto">
        {viewMode === 'week' ? (
          <WeekView
            days={days}
            employees={orderedEmployees}
            shifts={shifts}
            loading={loading}
            getAbsence={getAbsence}
            getShifts={getShifts}
            openModal={openModal}
            setModal={setModal}
            deleteShift={deleteShift}
            weather={weather}
            affluence={affluence}
            copiedShift={copiedShift}
            onCopyShift={setCopiedShift}
            onPasteShift={pasteShift}
          />
        ) : (
          <DayView
            day={selectedDay}
            employees={orderedEmployees}
            shifts={shifts}
            positions={positions}
            loading={loading}
            getAbsence={getAbsence}
            openModal={openModal}
            setModal={setModal}
            deleteShift={deleteShift}
          />
        )}

        {/* Legend */}
        {positions.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-4 items-center print:mt-2">
            {positions.map(p => (
              <div key={p.id} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: p.color }} />
                <span className="text-xs text-gray-600">{p.name}</span>
              </div>
            ))}
            {viewMode === 'week' && (
              <>
                <div className="flex items-center gap-1.5 ml-4 pl-4 border-l border-gray-200">
                  <Sun size={12} className="text-amber-400" />
                  <span className="text-xs text-gray-500">Matin = avant 12h</span>
                  <Moon size={12} className="text-orange-400 ml-2" />
                  <span className="text-xs text-gray-500">A-M = à partir de 12h</span>
                </div>
                <div className="flex items-center gap-1.5 ml-2">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="text-xs text-gray-500">Brouillon</span>
                </div>
              </>
            )}
            <div className="flex items-center gap-1.5 ml-2 pl-4 border-l border-gray-200">
              <div className="w-8 h-4 rounded text-center text-xs font-bold flex items-center justify-center" style={{ backgroundColor: '#3B82F620', border: '1px solid #3B82F650', color: '#3B82F6' }}>CP</div>
              <span className="text-xs text-gray-500">Absence approuvée</span>
            </div>
          </div>
        )}
      </div>

      {/* Modal sauvegarde modèle */}
      {saveTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSaveTemplateModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-gray-900">Sauvegarder comme modèle</h2>
              <button onClick={() => setSaveTemplateModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom du modèle</label>
              <input
                type="text"
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveAsTemplate()}
                placeholder="Ex : Semaine type été, Noël..."
                autoFocus
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-2">
                Les {shifts.length} shift{shifts.length !== 1 ? 's' : ''} de la semaine du {format(weekStart, 'd MMM', { locale: fr })} seront sauvegardés.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setSaveTemplateModal(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Annuler</button>
              <button onClick={saveAsTemplate} disabled={savingTemplate || !templateName.trim()}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {savingTemplate ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ordre d'affichage des employés */}
      {showOrderModal && (() => {
        const ROW_COLORS = [
          null,
          '#BAE6FD','#86EFAC','#D9F99D','#FCA5A5','#FECACA','#FEF08A',
          '#FDBA74','#CCFBF1','#D1D5DB','#9CA3AF','#A8A29E','#FEF9C3',
          '#E9D5FF','#FBCFE8','#C7D2FE','#99F6E4','#FED7AA','#F5D0FE',
        ]
        async function setRowColor(empId: string, color: string | null) {
          await api.put(`/employees/${empId}`, { rowColor: color })
          setEmployees(prev => prev.map(e => e.id === empId ? { ...e, rowColor: color } : e))
        }
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowOrderModal(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-4 border-b">
                <div className="flex items-center gap-2">
                  <GripVertical size={18} className="text-gray-500" />
                  <h2 className="font-bold text-gray-900">Ordre & couleurs</h2>
                </div>
                <button onClick={() => setShowOrderModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
              <div className="p-4 space-y-1 max-h-[65vh] overflow-auto">
                {orderedEmployees.map((emp, idx) => (
                  <div key={emp.id} className="rounded-lg border border-gray-100 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2" style={emp.rowColor ? { backgroundColor: emp.rowColor } : {}}>
                      <GripVertical size={14} className="text-gray-400 flex-shrink-0" />
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                        {emp.firstName[0]}{emp.lastName[0]}
                      </div>
                      <span className="flex-1 text-sm font-medium text-gray-800">{emp.firstName} {emp.lastName}</span>
                      <div className="flex gap-0.5">
                        <button onClick={() => moveEmployee(emp.id, -1)} disabled={idx === 0}
                          className="p-1 rounded hover:bg-white/60 text-gray-500 disabled:opacity-30 transition-colors">
                          <ArrowUp size={13} />
                        </button>
                        <button onClick={() => moveEmployee(emp.id, 1)} disabled={idx === orderedEmployees.length - 1}
                          className="p-1 rounded hover:bg-white/60 text-gray-500 disabled:opacity-30 transition-colors">
                          <ArrowDown size={13} />
                        </button>
                      </div>
                    </div>
                    {/* Palette de couleurs */}
                    <div className="flex flex-wrap gap-1 px-3 pb-2 pt-1 bg-white/80">
                      {ROW_COLORS.map((color, ci) => (
                        <button key={ci} onClick={() => setRowColor(emp.id, color)}
                          title={color ?? 'Aucune couleur'}
                          className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 flex-shrink-0 ${emp.rowColor === color ? 'border-gray-700 scale-110' : 'border-white shadow-sm'}`}
                          style={{ backgroundColor: color ?? '#f9fafb', outline: color === null ? '1px dashed #9ca3af' : 'none' }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between px-5 py-3 border-t bg-gray-50 rounded-b-xl">
                <button onClick={() => saveEmployeeOrder([])} className="text-xs text-gray-500 hover:text-gray-700">Réinitialiser l'ordre</button>
                <button onClick={() => setShowOrderModal(false)} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">Fermer</button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Smart Planner Modal */}
      {smartPlannerOpen && modules.planningAuto && (
        <SmartPlannerModal
          employees={employees}
          positions={positions}
          establishmentId={establishment?.id}
          weekStart={format(weekStart, 'yyyy-MM-dd')}
          weather={weather}
          onClose={() => setSmartPlannerOpen(false)}
          onGenerate={handleSmartGenerate}
          onCycleGenerate={handleCycleGenerate}
        />
      )}

      {/* Modal */}
      {modal && (
        <ShiftModal
          modal={modal}
          positions={positions}
          employees={employees}
          onClose={() => setModal(null)}
          onSave={async (data) => {
            setSaving(true)
            try {
              if (modal.shift) {
                if (data._delete) {
                  await api.delete(`/shifts/${modal.shift.id}`)
                  setShifts(prev => prev.filter(s => s.id !== modal.shift!.id))
                } else {
                  const res = await api.put(`/shifts/${modal.shift.id}`, data)
                  setShifts(prev => prev.map(s => s.id === modal.shift!.id ? res.data : s))
                }
              } else {
                const res = await api.post('/shifts', {
                  ...data,
                  employeeId: modal.employeeId,
                  establishmentId: establishment?.id,
                  date: modal.date
                })
                setShifts(prev => [...prev, res.data])
              }
              setModal(null)
            } finally {
              setSaving(false)
            }
          }}
          saving={saving}
        />
      )}

      {/* Modal import CA N-1 */}
      {showExcelImport && establishment && (
        <ImportPlanningExcel
          establishmentId={establishment.id}
          onClose={() => setShowExcelImport(false)}
          onImported={() => { load(); loadLastImport() }}
        />
      )}

      {showRevenueImport && establishment && (
        <RevenueImportModal
          establishmentId={establishment.id}
          onClose={() => setShowRevenueImport(false)}
        />
      )}
    </div>
  )
}

// ── Vue semaine ──
function WeekView({ days, employees, shifts, loading, getAbsence, getShifts, openModal, setModal, deleteShift, weather, affluence, copiedShift, onCopyShift, onPasteShift }: {
  days: Date[]; employees: Employee[]; shifts: Shift[]; loading: boolean
  getAbsence: (empId: string, dateStr: string) => Absence | null
  getShifts: (empId: string, date: Date, half: HalfDay) => Shift[]
  openModal: (empId: string, date: Date, half: HalfDay) => void
  setModal: (m: ModalData) => void
  deleteShift: (id: string) => void
  weather?: WeatherDay[] | null
  affluence?: { date: string; factor: number | null; caN1: number | null }[] | null
  copiedShift: Shift | null
  onCopyShift: (shift: Shift) => void
  onPasteShift: (empId: string, dateStr: string) => void
}) {
  function hasDailyAlert(empId: string): boolean {
    return days.some(day => dayNetHours(shifts, empId, format(day, 'yyyy-MM-dd')) > 10)
  }
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{ minWidth: '1100px' }}>
      {/* Header row: Affluence IA (facteur CA N-1 — même moteur que la génération) */}
      {affluence && affluence.length === 7 && affluence.some(a => a.factor != null) && (
        <div className="grid border-b border-violet-100 bg-violet-50/40 print:hidden" style={{ gridTemplateColumns: GRID }}>
          <div className="px-3 py-1 text-[11px] text-violet-500 font-semibold border-r border-gray-200 flex items-center gap-1 select-none">
            🧠 Affluence
          </div>
          {affluence.map((a, i) => {
            const f = a.factor
            const label = f == null ? '—' : f >= 0.75 ? '🔥 Forte' : f >= 0.4 ? '● Moyenne' : '○ Calme'
            const color = f == null ? 'text-gray-400' : f >= 0.75 ? 'text-red-600' : f >= 0.4 ? 'text-amber-600' : 'text-emerald-600'
            const title = f == null ? 'Pas de CA N-1' : `Facteur ${(f * 100).toFixed(0)} % — CA N-1 : ${a.caN1 != null ? Math.round(a.caN1).toLocaleString('fr-FR') + ' €' : '—'}`
            return (
              <div key={i} className="border-r border-gray-200 py-1 px-1.5 flex items-center justify-between" style={{ gridColumn: 'span 2' }} title={title}>
                <span className={`text-[11px] font-bold ${color}`}>{label}</span>
                {f != null && (
                  <span className="flex-1 ml-1.5 h-1.5 rounded bg-violet-100 overflow-hidden">
                    <span className="block h-full rounded bg-violet-400" style={{ width: `${Math.round(f * 100)}%` }} />
                  </span>
                )}
              </div>
            )
          })}
          <div />
        </div>
      )}
      {/* Header row: Météo */}
      {weather && weather.length === 7 && (<>
        <div className="grid border-b border-sky-100 bg-sky-50/40 print:hidden" style={{ gridTemplateColumns: GRID }}>
          <div className="px-3 py-1 text-[11px] text-sky-500 font-semibold border-r border-gray-200 flex items-center gap-1 select-none">
            <Cloud size={11} /> Météo
          </div>
          {days.map((_, i) => {
            const w = weather[i] as any
            return (
              <div key={i} className="border-r border-gray-200 py-1 px-1.5 flex justify-between items-center" style={{ gridColumn: 'span 2' }}>
                <div className="flex items-center gap-0.5" title={w?.forecast?.label}>
                  <span className="text-[9px] font-bold text-sky-500 mr-0.5">J</span>
                  <span className="text-sm leading-none">{w?.forecast?.icon ?? '—'}</span>
                  <span className="text-[11px] font-bold text-gray-800">{w?.forecast?.tmax != null ? `${Math.round(w.forecast.tmax)}°` : ''}</span>
                </div>
                <div className="flex items-center gap-0.5 opacity-70" title={w?.n1?.weather?.label}>
                  <span className="text-[9px] font-bold text-gray-400 mr-0.5">N-1</span>
                  <span className="text-xs leading-none">{w?.n1?.weather?.icon ?? '—'}</span>
                  <span className="text-[10px] text-gray-400">{w?.n1?.weather?.tmax != null ? `${Math.round(w.n1.weather.tmax)}°` : ''}</span>
                </div>
              </div>
            )
          })}
          <div />
        </div>

        {/* Header row: Stats CA / Panier / Clients */}
        <div className="grid border-b border-amber-100 bg-amber-50/30 print:hidden" style={{ gridTemplateColumns: GRID }}>
          <div className="px-3 py-1 text-[11px] text-amber-600 font-semibold border-r border-gray-200 flex items-center gap-1 select-none">
            <Euro size={11} /> Stats
          </div>
          {days.map((_, i) => {
            const w = weather[i] as any
            const revN  = w?.revenueN ?? null
            const revN1 = w?.n1?.revenue ?? null
            const clientsN  = w?.clientsN ?? null
            const clientsN1 = w?.n1?.clients ?? null
            const panierN   = w?.panierMoyenN ?? null
            const panierN1  = w?.n1?.panierMoyen ?? null
            const fmtCA = (v: number | null) => v != null ? (v >= 1000 ? `${(v/1000).toFixed(1)}k€` : `${Math.round(v)}€`) : '—'
            return (
              <div key={i} className="border-r border-gray-200 py-0.5 px-1" style={{ gridColumn: 'span 2' }}>
                {/* Labels J / N-1 */}
                <div className="flex justify-between text-[9px] font-bold mb-0.5 px-0.5">
                  <span className="text-blue-500">J</span>
                  <span className="text-gray-400">N-1</span>
                </div>
                {/* CA */}
                <div className="flex justify-between items-center px-0.5">
                  <span className="text-[11px] font-bold text-blue-600">{fmtCA(revN)}</span>
                  <span className="text-[11px] font-semibold text-emerald-600">{fmtCA(revN1)}</span>
                </div>
                {/* Panier moyen */}
                <div className="flex justify-between items-center px-0.5">
                  <span className="text-[9px] text-blue-400">🛒 {panierN != null ? panierN.toFixed(2) + '€' : '—'}</span>
                  <span className="text-[9px] text-gray-400">{panierN1 != null ? panierN1.toFixed(2) + '€' : '—'}</span>
                </div>
                {/* Clients */}
                <div className="flex justify-between items-center px-0.5">
                  <span className="text-[9px] text-blue-400">👥 {clientsN ?? '—'}</span>
                  <span className="text-[9px] text-gray-400">{clientsN1 ?? '—'}</span>
                </div>
              </div>
            )
          })}
          <div />
        </div>
      </>)}

      {/* Header: jours + dates (entre météo et employés) */}
      <div className="grid border-b border-gray-200" style={{ gridTemplateColumns: GRID }}>
        <div className={`px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-r ${DAY_SEP}`} />
        {days.map((day, i) => {
          const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
          return (
            <div key={i}
              className={`text-center border-r ${DAY_SEP} py-2 ${isToday ? 'bg-blue-50' : ''}`}
              style={{ gridColumn: 'span 2' }}>
              <p className={`text-xs font-semibold uppercase tracking-wide ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
                {DAYS[i]}
              </p>
              <p className={`text-sm font-bold mt-0.5 ${isToday ? 'text-blue-700' : 'text-gray-800'}`}>
                {format(day, 'd')}
              </p>
            </div>
          )
        })}
        <div className="px-2 py-2 text-center">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</p>
        </div>
      </div>

      {/* Header row 2: Matin / Après-midi */}
      <div className="grid border-b border-gray-200 bg-gray-50" style={{ gridTemplateColumns: GRID }}>
        <div className={`border-r ${DAY_SEP}`} />
        {days.map((_, i) => {
          const isToday = format(days[i], 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
          return (
            <>
              <div key={`${i}-m`}
                className={`flex items-center justify-center gap-1 py-1.5 border-r border-gray-100 ${isToday ? 'bg-blue-50/60' : ''}`}>
                <Sun size={11} className="text-amber-400" />
                <span className="text-xs text-gray-400 font-medium">Matin</span>
              </div>
              <div key={`${i}-am`}
                className={`flex items-center justify-center gap-1 py-1.5 border-r ${DAY_SEP} ${isToday ? 'bg-blue-50/60' : ''}`}>
                <Moon size={11} className="text-orange-400" />
                <span className="text-xs text-gray-400 font-medium">A-M</span>
              </div>
            </>
          )
        })}
        <div />
      </div>

      {/* Employee rows */}
      {loading ? (
        <div className="py-16 text-center text-gray-400 text-sm">Chargement...</div>
      ) : employees.length === 0 ? (
        <div className="py-16 text-center text-gray-400 text-sm">Aucun employé trouvé</div>
      ) : (
        employees.map(emp => {
          const total = weekHours(shifts, emp.id)
          const over = total > emp.weeklyHours
          return (
            <div key={emp.id}
              className="grid border-b border-gray-100 last:border-b-0 hover:bg-gray-50/30 transition-colors"
              style={{ gridTemplateColumns: GRID, ...(emp.rowColor ? { backgroundColor: emp.rowColor } : {}) }}>
              <div className={`px-2 py-1 flex items-center border-r ${DAY_SEP}`}>
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 mr-1.5">
                  {emp.firstName[0]}{emp.lastName[0]}
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-gray-900 truncate leading-tight">{emp.firstName} {emp.lastName}</p>
                  <p className="text-[10px] text-gray-400 leading-tight">{emp.weeklyHours}h/sem.</p>
                </div>
              </div>
              {days.map((day, i) => {
                const dateStr = format(day, 'yyyy-MM-dd')
                const isToday = dateStr === format(new Date(), 'yyyy-MM-dd')
                const absence = getAbsence(emp.id, dateStr)
                return (
                  <>
                    <HalfCell
                      key={`${emp.id}-${i}-m`}
                      shifts={getShifts(emp.id, day, 'morning')}
                      isToday={isToday}
                      borderRight="border-gray-100"
                      absence={absence}
                      onAdd={() => openModal(emp.id, day, 'morning')}
                      onEdit={shift => setModal({ employeeId: emp.id, date: dateStr, halfDay: 'morning', shift })}
                      onDelete={deleteShift}
                      onCopy={shift => onCopyShift(shift)}
                      onPaste={() => onPasteShift(emp.id, dateStr)}
                      hasCopied={!!copiedShift}
                    />
                    <HalfCell
                      key={`${emp.id}-${i}-am`}
                      shifts={getShifts(emp.id, day, 'afternoon')}
                      isToday={isToday}
                      borderRight={DAY_SEP}
                      absence={absence}
                      onAdd={() => openModal(emp.id, day, 'afternoon')}
                      onEdit={shift => setModal({ employeeId: emp.id, date: dateStr, halfDay: 'afternoon', shift })}
                      onDelete={deleteShift}
                      onCopy={shift => onCopyShift(shift)}
                      onPaste={() => onPasteShift(emp.id, dateStr)}
                      hasCopied={!!copiedShift}
                    />
                  </>
                )
              })}
              <div className="px-2 py-2 flex flex-col items-center justify-center gap-0.5">
                <span className={`text-xs font-bold ${over ? 'text-red-600' : 'text-gray-700'}`}>
                  {total.toFixed(1)}h
                </span>
                {hasDailyAlert(emp.id) && (
                  <span title="Dépassement journalier détecté (> 10h)">
                    <AlertTriangle size={10} className="text-amber-500" />
                  </span>
                )}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

// ── Vue journée ──
function DayView({ day, employees, shifts, positions, loading, getAbsence, openModal, setModal, deleteShift }: {
  day: Date; employees: Employee[]; shifts: Shift[]; positions: Position[]; loading: boolean
  getAbsence: (empId: string, dateStr: string) => Absence | null
  openModal: (empId: string, date: Date, half: HalfDay) => void
  setModal: (m: ModalData) => void
  deleteShift: (id: string) => void
}) {
  const [showOff, setShowOff] = useState(false)
  const dateStr = format(day, 'yyyy-MM-dd')
  const dayShifts = shifts.filter(s => s.date === dateStr)
  const totalHours = dayShifts.reduce((sum, s) => sum + shiftDuration(s), 0)

  if (loading) return <div className="py-16 text-center text-gray-400 text-sm">Chargement...</div>

  // Deux blocs : Matin (horaire qui commence avant midi) puis Après-midi.
  // Une journée coupée apparaît dans les deux, chaque fois avec le bon horaire.
  const shiftsOf = (id: string, half: HalfDay) => dayShifts
    .filter(s => s.employeeId === id && (half === 'morning' ? isMorning(s) : !isMorning(s)))
    .sort((a, b) => a.startTime.localeCompare(b.startTime))
  // Même ordre que les lignes du planning Excel (liste déjà triée par la page)
  const inHalf = (half: HalfDay) => employees.filter(e => shiftsOf(e.id, half).length > 0)
  const matin = inHalf('morning')
  const aprem = inHalf('afternoon')
  const absents = employees.filter(e => getAbsence(e.id, dateStr) && !dayShifts.some(s => s.employeeId === e.id))
  const off = employees.filter(e => !dayShifts.some(s => s.employeeId === e.id) && !absents.includes(e))
  const dayTotalOf = (id: string) => dayShifts.filter(s => s.employeeId === id).reduce((sum, s) => sum + shiftDuration(s), 0)

  const who = (emp: Employee, total?: number) => (
    <div className="flex items-center gap-3 sm:w-48 flex-shrink-0">
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-gray-900 border border-black/10"
        style={{ backgroundColor: emp.rowColor || '#E5E7EB' }}>
        {emp.firstName[0]}{emp.lastName?.[0] || ''}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 truncate">{emp.firstName} {emp.lastName}</p>
        <p className="text-xs text-gray-400">{emp.weeklyHours}h/sem.</p>
      </div>
      {total != null && total > 0 && (
        <span className={`sm:hidden text-xs font-semibold ${total > 10 ? 'text-red-600' : 'text-gray-500'}`} title="Total de la journée">{total.toFixed(1)}h/j</span>
      )}
    </div>
  )

  const row = (emp: Employee, half: HalfDay | null, dimmed = false) => {
    const list = half ? shiftsOf(emp.id, half) : []
    const absence = getAbsence(emp.id, dateStr)
    const total = dayTotalOf(emp.id)
    return (
      <div key={`${emp.id}-${half || 'x'}`}
        className={`px-3 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 hover:bg-gray-50/50 transition-colors ${dimmed ? 'opacity-70' : ''}`}
        style={{ borderLeft: `6px solid ${emp.rowColor || 'transparent'}` }}>
        {who(emp, half ? total : undefined)}
        {absence && !half ? (
          <div className="flex-1">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium"
              style={{ backgroundColor: absence.absenceType.color + '20', color: absence.absenceType.color, border: `1px solid ${absence.absenceType.color}40` }}>
              {absence.absenceType.code} — {absence.absenceType.name}
            </span>
          </div>
        ) : (
          <div className="flex-1 flex flex-wrap gap-2">
            {list.map(shift => (
              <div key={shift.id}
                role="button"
                onClick={() => setModal({ employeeId: emp.id, date: dateStr, halfDay: isMorning(shift) ? 'morning' : 'afternoon', shift })}
                className="group relative flex items-center gap-2 px-3 py-2 rounded-lg text-white text-sm font-medium shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                style={{ backgroundColor: shift.position?.color || '#6B7280' }}>
                <div>
                  <div className="font-bold text-sm">{shift.startTime}–{shift.endTime}</div>
                  {shift.position && <div className="text-white/80 text-xs">{shift.position.name}</div>}
                </div>
                <button
                  onClick={e => { e.stopPropagation(); deleteShift(shift.id) }}
                  className="ml-1 sm:opacity-0 group-hover:opacity-100 transition-opacity text-white/70 hover:text-white">
                  <X size={12} />
                </button>
                {shift.status === 'draft' && (
                  <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-amber-300 rounded-full" />
                )}
              </div>
            ))}
            <button
              onClick={() => openModal(emp.id, day, half || 'morning')}
              className="flex items-center gap-1 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400 text-sm transition-colors">
              <Plus size={13} /> Ajouter
            </button>
          </div>
        )}
        {half && total > 0 && (
          <div className="hidden sm:block ml-auto flex-shrink-0 text-right">
            <span className={`text-sm font-bold ${total > 10 ? 'text-red-600' : 'text-gray-600'}`}>{total.toFixed(1)}h</span>
            <p className="text-[10px] text-gray-400">sur la journée</p>
          </div>
        )}
      </div>
    )
  }

  const section = (title: React.ReactNode, count: number, tone: string) => (
    <div className={`px-3 sm:px-6 py-2 flex items-center justify-between text-sm font-bold border-y-2 border-gray-900 ${tone}`}>
      <span className="flex items-center gap-2">{title}</span>
      <span className="text-xs font-semibold opacity-70">{count} personne{count > 1 ? 's' : ''}</span>
    </div>
  )

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-3 sm:px-6 py-3 bg-gray-50 flex items-center justify-between flex-wrap gap-1">
        <h2 className="text-sm font-semibold text-gray-800 capitalize">
          {format(day, 'EEEE d MMMM yyyy', { locale: fr })}
        </h2>
        <div className="text-sm text-gray-500">{totalHours.toFixed(1)}h au total</div>
      </div>

      {employees.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm">Aucun employé trouvé</div>
      ) : (
        <>
          {section(<><Sun size={15} className="text-amber-500" /> Matin</>, matin.length, 'bg-amber-50 text-amber-900')}
          <div className="divide-y divide-gray-100">
            {matin.length === 0 && <div className="py-6 text-center text-gray-400 text-sm">Personne le matin</div>}
            {matin.map(emp => row(emp, 'morning'))}
          </div>

          {section(<><Moon size={15} className="text-indigo-500" /> Après-midi</>, aprem.length, 'bg-indigo-50 text-indigo-900')}
          <div className="divide-y divide-gray-100">
            {aprem.length === 0 && <div className="py-6 text-center text-gray-400 text-sm">Personne l’après-midi</div>}
            {aprem.map(emp => row(emp, 'afternoon'))}
          </div>

          {absents.length > 0 && (
            <>
              {section(<>Absents</>, absents.length, 'bg-gray-50 text-gray-700')}
              <div className="divide-y divide-gray-100">{absents.map(emp => row(emp, null))}</div>
            </>
          )}

          {off.length > 0 && (
            <div className="border-t-2 border-gray-900">
              <button onClick={() => setShowOff(v => !v)}
                className="w-full px-3 sm:px-6 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 hover:bg-gray-100">
                {showOff ? '▾' : '▸'} Sans horaire ce jour ({off.length})
              </button>
              {showOff && <div className="divide-y divide-gray-100">{off.map(emp => row(emp, 'morning', true))}</div>}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Composant cellule demi-journée ──
function HalfCell({ shifts, isToday, borderRight, onAdd, onEdit, onDelete, absence, onCopy, onPaste, hasCopied }: {
  shifts: Shift[]
  isToday: boolean
  borderRight: string
  absence?: Absence | null
  onAdd: () => void
  onEdit: (shift: Shift) => void
  onDelete: (id: string) => void
  onCopy?: (shift: Shift) => void
  onPaste?: () => void
  hasCopied?: boolean
}) {
  // Cas hérité : absence approuvée mais pas (encore) de shifts fantômes générés.
  // Les nouvelles absences créent automatiquement des shifts (option C choisie),
  // mais on garde ce fallback pour les anciennes absences sans shifts.
  if (absence && shifts.length === 0) {
    return (
      <div className={`px-1 py-0.5 border-r ${borderRight} min-h-[44px] ${isToday ? 'bg-blue-50/30' : ''}`}>
        <div className="h-full rounded px-1.5 py-1 flex flex-col items-center justify-center min-h-[36px]"
          style={{ backgroundColor: absence.absenceType.color + '25', border: `1px solid ${absence.absenceType.color}50` }}>
          <span className="text-xs font-bold" style={{ color: absence.absenceType.color }}>
            {absence.absenceType.code}
          </span>
        </div>
      </div>
    )
  }

  // Détection : au moins un shift sur cette demi-journée provient d'une absence.
  // Si oui, on rend les shifts normalement (couleur du poste + horaires visibles)
  // et on superpose un calque transparent avec le code (CP/MAL/...).
  const absShift = shifts.find(s => !!s.absenceId)
  const overlayColor = absShift?.absence?.absenceType.color
  const overlayCode  = absShift?.absence?.absenceType.code

  return (
    <div
      className={`px-1 py-0.5 border-r ${borderRight} min-h-[44px] cursor-pointer group transition-colors relative ${isToday ? 'bg-blue-50/30' : 'hover:bg-gray-50'}`}
      onClick={onAdd}>
      {shifts.map(shift => {
        const isAbsenceShift = !!shift.absenceId
        const bgColor = shift.position?.color || '#6B7280'
        return (
          <div key={shift.id}
            className="rounded px-1.5 py-1 mb-0.5 text-white relative group/shift cursor-pointer"
            style={{ backgroundColor: bgColor }}
            onClick={e => { e.stopPropagation(); onEdit(shift) }}
            title={isAbsenceShift && shift.absence ? `${shift.absence.absenceType.name} — heures comptabilisées` : undefined}>
            <button
              onClick={e => { e.stopPropagation(); onDelete(shift.id) }}
              className="absolute top-0.5 right-0.5 opacity-0 group-hover/shift:opacity-100 transition-opacity hover:text-red-200 z-10">
              <X size={10} />
            </button>
            {onCopy && !isAbsenceShift && (
              <button
                onClick={e => { e.stopPropagation(); onCopy(shift) }}
                className="absolute bottom-0.5 right-0.5 opacity-0 group-hover/shift:opacity-100 transition-opacity hover:text-yellow-200 z-10"
                title="Copier ce shift">
                <Copy size={9} />
              </button>
            )}
            <p className="font-bold leading-tight whitespace-nowrap" style={{ fontSize: '11px' }}>
              {shift.startTime}–{shift.endTime}
            </p>
            {shift.position && (
              <p className="text-white/70 truncate leading-tight mt-0.5" style={{ fontSize: '10px' }}>
                {shift.position.name}
              </p>
            )}
            {shift.status === 'draft' && !isAbsenceShift && (
              <div className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-amber-300 rounded-full" title="Brouillon" />
            )}
            {shift.sourceGeneration === 'generated' && !isAbsenceShift && (
              <div className="absolute top-0.5 left-0.5 opacity-60" title="Généré automatiquement">
                <Zap size={8} className="text-white" />
              </div>
            )}
          </div>
        )
      })}
      {overlayColor && overlayCode && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none rounded"
          style={{
            backgroundColor: overlayColor + '55',           // ~33% opacité
            border: `2px dashed ${overlayColor}`,
          }}>
          <span
            className="font-extrabold text-base uppercase tracking-widest drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]"
            style={{ color: '#FFFFFF', textShadow: `0 0 4px ${overlayColor}` }}>
            {overlayCode}
          </span>
        </div>
      )}
      {shifts.length === 0 && (
        <div className="flex items-center justify-center h-full opacity-0 group-hover:opacity-100 transition-opacity py-3 gap-1">
          {hasCopied && onPaste ? (
            <button onClick={e => { e.stopPropagation(); onPaste() }}
              className="flex items-center gap-0.5 text-blue-500 hover:text-blue-700 text-xs font-medium"
              title="Coller le shift copié">
              <Copy size={11} />
            </button>
          ) : (
            <Plus size={13} className="text-gray-400" />
          )}
        </div>
      )}
    </div>
  )
}

// ── Modal création / modification ──
function ShiftModal({ modal, positions, employees, onClose, onSave, saving }: {
  modal: ModalData; positions: Position[]; employees: Employee[]
  onClose: () => void; onSave: (data: any) => void; saving: boolean
}) {
  const emp = employees.find(e => e.id === modal.employeeId)
  const defaultStart = modal.shift?.startTime || (modal.halfDay === 'morning' ? '09:00' : '14:00')
  const defaultEnd  = modal.shift?.endTime   || (modal.halfDay === 'morning' ? '13:00' : '22:00')

  const [startTime, setStartTime] = useState(defaultStart)
  const [endTime, setEndTime] = useState(defaultEnd)
  const [breakMinutes, setBreakMinutes] = useState(modal.shift?.breakMinutes ?? 20)
  const [positionId, setPositionId] = useState(modal.shift?.positionId || '')
  const [note, setNote] = useState(modal.shift?.note || '')

  const label = 'block text-sm font-medium text-gray-700 mb-1'
  const input = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
  const halfLabel = modal.halfDay === 'morning'
    ? <span className="flex items-center gap-1 text-amber-600"><Sun size={13} /> Équipe matin</span>
    : <span className="flex items-center gap-1 text-orange-500"><Moon size={13} /> Équipe après-midi</span>

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="font-bold text-gray-900">{modal.shift ? 'Modifier le shift' : 'Nouveau shift'}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {emp?.firstName} {emp?.lastName} — {format(new Date(modal.date + 'T00:00'), 'EEEE d MMMM', { locale: fr })}
            </p>
            <p className="text-xs mt-1">{halfLabel}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Début</label>
              <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className={input} />
            </div>
            <div>
              <label className={label}>Fin</label>
              <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className={input} />
            </div>
          </div>
          <div>
            <label className={label}>Pause (minutes)</label>
            <input type="number" value={breakMinutes} onChange={e => setBreakMinutes(Number(e.target.value))} min={0} max={120} className={input} />
          </div>
          <div>
            <label className={label}>Poste</label>
            <select value={positionId} onChange={e => setPositionId(e.target.value)} className={input}>
              <option value="">-- Sans poste --</option>
              {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Note (optionnel)</label>
            <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Remarque..." className={input} />
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          {modal.shift ? (
            <button onClick={() => onSave({ _delete: true })}
              className="flex items-center gap-1.5 text-red-600 hover:text-red-800 text-sm font-medium transition-colors">
              <Trash2 size={15} /> Supprimer
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              Annuler
            </button>
            <button
              onClick={() => onSave({ startTime, endTime, breakMinutes, positionId: positionId || null, note: note || null })}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
              {saving ? 'Enregistrement...' : modal.shift ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Smart Planner Modal ──────────────────────────────────────────
const SP_DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

interface DaySlotConfig {
  morningEnabled: boolean; morningMin: number; morningMax: number
  afternoonEnabled: boolean; afternoonMin: number; afternoonMax: number
}

interface EvenementLocal { id: string; nom: string; lieu: string; jour: number; mois: number; annee?: number; recurrent: boolean }

// ── Jours fériés France + Vacances scolaires ────────────────────────────────
function easterSunday(year: number): Date {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const mo = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(Date.UTC(year, mo - 1, day))
}
function addDU(d: Date, n: number): Date { const r = new Date(d); r.setUTCDate(r.getUTCDate() + n); return r }
function fmtDU(d: Date): string { return d.toISOString().split('T')[0] }

function frenchHolidays(year: number): { date: string; name: string }[] {
  const p = easterSunday(year)
  return [
    { date: `${year}-01-01`, name: "Jour de l'an" },
    { date: fmtDU(addDU(p, 1)), name: 'Lundi de Pâques' },
    { date: `${year}-05-01`, name: 'Fête du Travail' },
    { date: `${year}-05-08`, name: 'Victoire 1945' },
    { date: fmtDU(addDU(p, 39)), name: 'Ascension' },
    { date: fmtDU(addDU(p, 50)), name: 'Lundi de Pentecôte' },
    { date: `${year}-07-14`, name: 'Fête Nationale' },
    { date: `${year}-08-15`, name: 'Assomption' },
    { date: `${year}-11-01`, name: 'Toussaint' },
    { date: `${year}-11-11`, name: 'Armistice' },
    { date: `${year}-12-25`, name: 'Noël' },
  ]
}

// Vacances scolaires toutes zones — pour zones touristiques les 3 zones comptent
const VACANCES_FR: { zone: string; name: string; start: string; end: string }[] = [
  // ─── Toussaint (toutes zones identiques) ───
  { zone: 'A', name: 'Toussaint 2025', start: '2025-10-18', end: '2025-11-03' },
  { zone: 'B', name: 'Toussaint 2025', start: '2025-10-18', end: '2025-11-03' },
  { zone: 'C', name: 'Toussaint 2025', start: '2025-10-18', end: '2025-11-03' },
  // ─── Noël 2025 (toutes zones identiques) ───
  { zone: 'A', name: 'Noël 2025-26', start: '2025-12-20', end: '2026-01-05' },
  { zone: 'B', name: 'Noël 2025-26', start: '2025-12-20', end: '2026-01-05' },
  { zone: 'C', name: 'Noël 2025-26', start: '2025-12-20', end: '2026-01-05' },
  // ─── Hiver 2026 ───
  { zone: 'A', name: 'Hiver 2026', start: '2026-02-07', end: '2026-02-23' },
  { zone: 'B', name: 'Hiver 2026', start: '2026-02-21', end: '2026-03-09' },
  { zone: 'C', name: 'Hiver 2026', start: '2026-02-14', end: '2026-03-02' },
  // ─── Printemps 2026 ───
  { zone: 'A', name: 'Printemps 2026', start: '2026-04-18', end: '2026-05-04' },
  { zone: 'B', name: 'Printemps 2026', start: '2026-04-04', end: '2026-04-20' },
  { zone: 'C', name: 'Printemps 2026', start: '2026-04-11', end: '2026-04-27' },
  // ─── Été 2026 (départ échelonné) ───
  { zone: 'A', name: 'Été 2026', start: '2026-07-04', end: '2026-08-31' },
  { zone: 'B', name: 'Été 2026', start: '2026-07-04', end: '2026-08-31' },
  { zone: 'C', name: 'Été 2026', start: '2026-07-04', end: '2026-08-31' },
  // ─── Toussaint 2026 ───
  { zone: 'A', name: 'Toussaint 2026', start: '2026-10-17', end: '2026-11-02' },
  { zone: 'B', name: 'Toussaint 2026', start: '2026-10-17', end: '2026-11-02' },
  { zone: 'C', name: 'Toussaint 2026', start: '2026-10-17', end: '2026-11-02' },
  // ─── Noël 2026 ───
  { zone: 'A', name: 'Noël 2026-27', start: '2026-12-19', end: '2027-01-04' },
  { zone: 'B', name: 'Noël 2026-27', start: '2026-12-19', end: '2027-01-04' },
  { zone: 'C', name: 'Noël 2026-27', start: '2026-12-19', end: '2027-01-04' },
  // ─── Hiver 2027 ───
  { zone: 'A', name: 'Hiver 2027', start: '2027-02-06', end: '2027-02-22' },
  { zone: 'B', name: 'Hiver 2027', start: '2027-02-20', end: '2027-03-08' },
  { zone: 'C', name: 'Hiver 2027', start: '2027-02-13', end: '2027-03-01' },
  // ─── Printemps 2027 ───
  { zone: 'A', name: 'Printemps 2027', start: '2027-04-17', end: '2027-05-03' },
  { zone: 'B', name: 'Printemps 2027', start: '2027-04-03', end: '2027-04-19' },
  { zone: 'C', name: 'Printemps 2027', start: '2027-04-10', end: '2027-04-26' },
]

const VACANCES_ETRANGERES: { name: string; start: string; end: string; pays: string }[] = [
  // 🇬🇧 Royaume-Uni (grandes vacances d'été + demi-trimestres)
  { name: 'Été', start: '2025-07-18', end: '2025-09-02', pays: '🇬🇧 Royaume-Uni' },
  { name: 'Demi-trimestre oct.', start: '2025-10-25', end: '2025-11-02', pays: '🇬🇧 Royaume-Uni' },
  { name: 'Noël', start: '2025-12-20', end: '2026-01-05', pays: '🇬🇧 Royaume-Uni' },
  { name: 'Pâques', start: '2026-04-03', end: '2026-04-17', pays: '🇬🇧 Royaume-Uni' },
  { name: 'Été', start: '2026-07-17', end: '2026-09-01', pays: '🇬🇧 Royaume-Uni' },
  // 🇳🇱 Pays-Bas
  { name: 'Été', start: '2025-07-05', end: '2025-08-17', pays: '🇳🇱 Pays-Bas' },
  { name: 'Vacances d\'automne', start: '2025-10-18', end: '2025-10-26', pays: '🇳🇱 Pays-Bas' },
  { name: 'Été', start: '2026-07-04', end: '2026-08-16', pays: '🇳🇱 Pays-Bas' },
  // 🇩🇪 Allemagne (Bayern = plus longues)
  { name: 'Été', start: '2025-07-04', end: '2025-09-05', pays: '🇩🇪 Allemagne' },
  { name: 'Automne', start: '2025-10-27', end: '2025-11-08', pays: '🇩🇪 Allemagne' },
  { name: 'Noël', start: '2025-12-22', end: '2026-01-05', pays: '🇩🇪 Allemagne' },
  { name: 'Été', start: '2026-07-03', end: '2026-09-04', pays: '🇩🇪 Allemagne' },
  // 🇧🇪 Belgique
  { name: 'Été', start: '2025-07-01', end: '2025-08-31', pays: '🇧🇪 Belgique' },
  { name: 'Toussaint', start: '2025-11-01', end: '2025-11-09', pays: '🇧🇪 Belgique' },
  { name: 'Noël', start: '2025-12-27', end: '2026-01-04', pays: '🇧🇪 Belgique' },
  { name: 'Été', start: '2026-07-01', end: '2026-08-31', pays: '🇧🇪 Belgique' },
  // 🇨🇭 Suisse
  { name: 'Été', start: '2025-07-07', end: '2025-08-10', pays: '🇨🇭 Suisse' },
  { name: 'Automne', start: '2025-10-04', end: '2025-10-19', pays: '🇨🇭 Suisse' },
  { name: 'Été', start: '2026-07-06', end: '2026-08-09', pays: '🇨🇭 Suisse' },
  // 🇪🇸 Espagne (été très long)
  { name: 'Été', start: '2025-06-20', end: '2025-09-10', pays: '🇪🇸 Espagne' },
  { name: 'Noël', start: '2025-12-22', end: '2026-01-08', pays: '🇪🇸 Espagne' },
  { name: 'Été', start: '2026-06-20', end: '2026-09-10', pays: '🇪🇸 Espagne' },
  // 🇮🇹 Italie
  { name: 'Été', start: '2025-06-14', end: '2025-09-14', pays: '🇮🇹 Italie' },
  { name: 'Noël', start: '2025-12-23', end: '2026-01-06', pays: '🇮🇹 Italie' },
  { name: 'Été', start: '2026-06-13', end: '2026-09-13', pays: '🇮🇹 Italie' },
  // 🇸🇪 Scandinavie (Suède, été)
  { name: 'Été', start: '2025-06-14', end: '2025-08-17', pays: '🇸🇪 Scandinavie' },
  { name: 'Été', start: '2026-06-13', end: '2026-08-16', pays: '🇸🇪 Scandinavie' },
  // 🇺🇸 Américains (grandes vacances — présents sur la Côte d'Azur juillet-août)
  { name: 'Été', start: '2025-06-15', end: '2025-09-01', pays: '🇺🇸 Amérique du Nord' },
  { name: 'Été', start: '2026-06-15', end: '2026-09-01', pays: '🇺🇸 Amérique du Nord' },
]

function SmartPlannerModal({ employees, positions, establishmentId, weekStart, weather, onClose, onGenerate, onCycleGenerate }: {
  employees: Employee[]
  positions: Position[]
  establishmentId?: string
  weekStart?: string
  weather?: any[] | null
  onClose: () => void
  onGenerate: (params: any) => Promise<any>
  onCycleGenerate: (cycleStart: string, params: any) => Promise<any>
}) {
  const STORAGE_KEY = 'utiliai_planner_config'
  const RULES_KEY = `utiliai_rules_${establishmentId || 'default'}`

  const DEFAULT_RULES: BusinessRules = {
    openingHours: Array.from({ length: 7 }, () => ({ closed: false, open: '08:30', close: '20:00' })),
    deliveryPeriods: [
      { id: '1', label: 'Été (1 juin – 31 août)', startMonth: 6, startDay: 1, endMonth: 8, endDay: 31, days: [1, 3, 5] },
      { id: '2', label: 'Reste de l\'année', startMonth: 0, startDay: 0, endMonth: 0, endDay: 0, days: [1, 4] },
    ]
  }
  const DEFAULT_MATRIX: DaySlotConfig[] = Array.from({ length: 7 }, () => ({
    morningEnabled: true, morningMin: 2, morningMax: 5,
    afternoonEnabled: true, afternoonMin: 1, afternoonMax: 3
  }))
  const DEFAULT_SKILLS = ['Fruits & Légumes','Caisse','Épicerie','Charcuterie','Boucherie','Poissonnerie','Boulangerie','Rayon','Réception livraison','Ménage','Responsable','Accueil']

  function loadSaved() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') } catch { return null }
  }
  function loadRules(): BusinessRules {
    try { return JSON.parse(localStorage.getItem(RULES_KEY) || 'null') ?? DEFAULT_RULES } catch { return DEFAULT_RULES }
  }
  const saved = loadSaved()

  const [tab, setTab] = useState<'rules' | 'slots' | 'constraints' | 'employees' | 'needs' | 'config' | 'criteria'>('rules')
  const [rulesSection, setRulesSection] = useState<'magasin' | 'saisons' | 'creneaux' | 'livraison' | 'humain' | 'scoring' | 'employes'>('magasin')
  const [rules, setRules] = useState<BusinessRules>(loadRules)

  // ── Config planning IA (chargée depuis le backend) ─────────────────────────
  const CONFIG_DEFAULTS_LOCAL = {
    saison_haute_debut: '06-01', saison_haute_fin: '08-31',
    livraison_basse_jours: [2, 5], livraison_basse_min: 4, livraison_basse_ideal: 5,
    livraison_haute_jours: [2, 4, 6], livraison_haute_min: 4, livraison_haute_ideal: 6,
    repos_min_heures: 11, amplitude_max_heures: 12, tolerance_heures: 0,
    pause_incluse: true, resp_prioritaire: true, groupes_lies: [] as string[][],
    binomes: [] as string[][], saisonnier_cdi_required: true,
    evenements_locaux: [] as EvenementLocal[],
    poids_sous_couverture: 10000, poids_ecart_contrat: 5000, poids_livraison: 3000,
    poids_priorite_poste: 1000, poids_competence: 500, poids_equite: 200, poids_stabilite: 100,
    pays_etrangers_actifs: ['🇬🇧 Royaume-Uni','🇳🇱 Pays-Bas','🇩🇪 Allemagne','🇧🇪 Belgique','🇨🇭 Suisse','🇪🇸 Espagne','🇮🇹 Italie','🇸🇪 Scandinavie','🇺🇸 Amérique du Nord'] as string[],
    min_employes_creneau: 2, ecart_max_matin_aprem: 1,
    heure_arrivee_preparation: '07:00', heure_depart_fermeture: '20:15', heure_livraison_matin: '06:00',
    competence_livraison: 'Réception livraison', min_cdi_par_saisonnier: 1,
    livraison_basse_min_palettes: 8, livraison_basse_max_palettes: 10, livraison_haute_max_palettes: 15,
    zone_vacances_scolaires: 'C',
  }
  const [planningConfig, setPlanningConfig] = useState<typeof CONFIG_DEFAULTS_LOCAL>(CONFIG_DEFAULTS_LOCAL)
  const [configLoading, setConfigLoading] = useState(false)
  const [configSaving, setConfigSaving] = useState(false)
  const [configSaved, setConfigSaved] = useState(false)
  const [newGroupInput, setNewGroupInput] = useState('')
  const [binomeEmp1, setBinomeEmp1] = useState('')
  const [binomeEmp2, setBinomeEmp2] = useState('')
  const [newEvt, setNewEvt] = useState<Omit<EvenementLocal,'id'>>({ nom: '', lieu: '', jour: 1, mois: 1, recurrent: true })

  useEffect(() => {
    setConfigLoading(true)
    api.get('/config/planning').then(r => setPlanningConfig({ ...CONFIG_DEFAULTS_LOCAL, ...r.data }))
      .catch(() => {})
      .finally(() => setConfigLoading(false))
  }, [])

  async function saveConfigToBackend() {
    setConfigSaving(true)
    try {
      await api.put('/config/planning', planningConfig)
      setConfigSaved(true)
      setTimeout(() => setConfigSaved(false), 2500)
    } catch { alert('Erreur lors de la sauvegarde') }
    finally { setConfigSaving(false) }
  }

  function updateConfig(key: string, value: any) {
    setPlanningConfig(prev => ({ ...prev, [key]: value }))
  }

  async function updateConfigAndSave(key: string, value: any) {
    setPlanningConfig(prev => ({ ...prev, [key]: value }))
    try { await api.put('/config/planning', { [key]: value }) } catch { alert('Erreur sauvegarde') }
  }

  function toggleLivraisonJour(key: 'livraison_basse_jours' | 'livraison_haute_jours', day: number) {
    setPlanningConfig(prev => {
      const arr: number[] = [...(prev[key] as number[])]
      const idx = arr.indexOf(day)
      if (idx >= 0) { arr.splice(idx, 1) } else { arr.push(day) }
      return { ...prev, [key]: arr.sort((a, b) => a - b) }
    })
  }
  const [morningCfg, setMorningCfg] = useState(saved?.morningCfg ?? { start: '07:00', end: '14:00', breakMinutes: 0 })
  const [afternoonCfg, setAfternoonCfg] = useState(saved?.afternoonCfg ?? { start: '13:15', end: '20:15', breakMinutes: 0 })
  const [dayMatrixBasse, setDayMatrixBasse] = useState<DaySlotConfig[]>(saved?.dayMatrixBasse ?? saved?.dayMatrix ?? DEFAULT_MATRIX)
  const [dayMatrixHaute, setDayMatrixHaute] = useState<DaySlotConfig[]>(saved?.dayMatrixHaute ?? saved?.dayMatrix ?? DEFAULT_MATRIX)
  const [seasonTab, setSeasonTab] = useState<'basse' | 'haute'>('basse')
  const [amplitudeMax, setAmplitudeMax] = useState(saved?.amplitudeMax ?? 12)
  const [restMinHours, setRestMinHours] = useState(saved?.restMinHours ?? 11)
  const [priorityMode, setPriorityMode] = useState<'equity' | 'cost' | 'coverage'>(saved?.priorityMode ?? 'equity')
  const [respectAvailabilities, setRespectAvailabilities] = useState(saved?.respectAvailabilities ?? true)
  const [weeklyHoursStrict, setWeeklyHoursStrict] = useState(saved?.weeklyHoursStrict ?? true)
  const [excludedIds, setExcludedIds] = useState<string[]>([])
  const [favoredIds, setFavoredIds] = useState<string[]>([])
  const [needs, setNeeds] = useState<Record<number, {skill: string; count: number; period: 'morning'|'afternoon'|'both'}[]>>(
    saved?.needs ?? Object.fromEntries(Array.from({length:7},(_,i)=>[i,[]]))
  )
  // customSkills = postes DB + extras localStorage non encore dans DB
  const dbSkillNames = positions.map(p => p.name)
  const extraSkills = (saved?.customSkills ?? DEFAULT_SKILLS).filter((s: string) => !dbSkillNames.includes(s))
  const [customSkills, setCustomSkills] = useState<string[]>([...dbSkillNames, ...extraSkills])
  const [newSkillInput, setNewSkillInput] = useState('')
  const [showSkillManager, setShowSkillManager] = useState(false)
  const [saved2, setSaved2] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [result, setResult] = useState<any>(null)

  // ─── AUTO-SAVE localStorage (debounce 500ms) ──────────────────────
  // Tout changement dans ces états est sauvegardé automatiquement.
  // Plus besoin de cliquer "Sauvegarder config".
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ morningCfg, afternoonCfg, dayMatrixBasse, dayMatrixHaute, amplitudeMax, restMinHours, priorityMode, respectAvailabilities, weeklyHoursStrict, needs, customSkills }))
        localStorage.setItem(RULES_KEY, JSON.stringify(rules))
        setSaved2(true)
        setTimeout(() => setSaved2(false), 1500)
      } catch { /* quota exceeded, ignore */ }
    }, 500)
    return () => clearTimeout(t)
  }, [morningCfg, afternoonCfg, dayMatrixBasse, dayMatrixHaute, amplitudeMax, restMinHours, priorityMode, respectAvailabilities, weeklyHoursStrict, needs, customSkills, rules])

  // ─── AUTO-SAVE planningConfig backend (debounce 1000ms) ──────────
  // Évite de spammer l'API quand on tape dans un input.
  useEffect(() => {
    if (configLoading) return            // pas tant que l'initial load n'a pas fini
    const t = setTimeout(() => {
      api.put('/config/planning', planningConfig).catch(() => { /* silent */ })
    }, 1000)
    return () => clearTimeout(t)
  }, [planningConfig, configLoading])

  // ── Contexte semaine (jours fériés, vacances, météo, CA N-1) ───────────────
  const weekDates = weekStart
    ? Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart + 'T12:00:00'); d.setDate(d.getDate() + i); return d.toISOString().split('T')[0] })
    : []
  const year = weekStart ? new Date(weekStart).getFullYear() : new Date().getFullYear()
  const allHolidays = [...frenchHolidays(year), ...frenchHolidays(year + 1)]
  const joursFerriesWeek = allHolidays.filter(h => weekDates.includes(h.date))
  // Toutes zones FR actives cette semaine (dédupliquées par name+zone)
  const vacancesFRWeek = VACANCES_FR.filter(v => weekDates.some(d => d >= v.start && d <= v.end))
  const paysActifs: string[] = (planningConfig as any).pays_etrangers_actifs ?? VACANCES_ETRANGERES.map(v => v.pays)
  const vacancesEtWeek = VACANCES_ETRANGERES.filter(v => paysActifs.includes(v.pays) && weekDates.some(d => d >= v.start && d <= v.end))
  const evtsLocaux: EvenementLocal[] = Array.isArray((planningConfig as any).evenements_locaux)
    ? (planningConfig as any).evenements_locaux : []
  const evtsLocauxWeek = evtsLocaux.filter(evt =>
    weekDates.some(d => {
      const [dy, dm, dd] = d.split('-').map(Number)
      return evt.recurrent ? (dm === evt.mois && dd === evt.jour) : (dy === evt.annee && dm === evt.mois && dd === evt.jour)
    })
  )
  const weekContext = {
    joursFeeries: joursFerriesWeek,
    vacancesFR: (() => {
      // Regrouper par name, lister les zones actives
      const byName: Record<string, string[]> = {}
      for (const v of vacancesFRWeek) { if (!byName[v.name]) byName[v.name] = []; byName[v.name].push(v.zone) }
      return Object.entries(byName).map(([name, zones]) => `${name} (Zone${zones.length > 1 ? 's' : ''} ${zones.join('+')})`)
    })(),
    vacancesEtrangeres: [...new Set(vacancesEtWeek.map(v => `${v.pays} (${v.name})`))],
    evenementsLocaux: evtsLocauxWeek.map(e => `${e.nom} à ${e.lieu}`).join(', '),
    meteo: (weather || []).map((w: any) => ({
      date: w.date,
      icon: w.forecast?.icon ?? null,
      label: w.forecast?.label ?? null,
      tmax: w.forecast?.tmax ?? null,
      caN1: w.n1?.revenue ?? null,
      iconN1: w.n1?.weather?.icon ?? null,
      labelN1: w.n1?.weather?.label ?? null,
      tmaxN1: w.n1?.weather?.tmax ?? null,
    })),
  }

  function saveConfig() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ morningCfg, afternoonCfg, dayMatrixBasse, dayMatrixHaute, amplitudeMax, restMinHours, priorityMode, respectAvailabilities, weeklyHoursStrict, needs, customSkills }))
    localStorage.setItem(RULES_KEY, JSON.stringify(rules))
    setSaved2(true)
    setTimeout(() => setSaved2(false), 2000)
  }

  function updateOpeningDay(i: number, patch: Partial<OpeningDay>) {
    setRules(r => ({ ...r, openingHours: r.openingHours.map((d, idx) => idx === i ? { ...d, ...patch } : d) }))
  }
  function updateDeliveryPeriod(id: string, patch: Partial<DeliveryPeriod>) {
    setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(p => p.id === id ? { ...p, ...patch } : p) }))
  }
  function addDeliveryPeriod() {
    setRules(r => ({ ...r, deliveryPeriods: [...r.deliveryPeriods, { id: Date.now().toString(), label: 'Nouvelle période', startMonth: 0, startDay: 0, endMonth: 0, endDay: 0, days: [] }] }))
  }
  function removeDeliveryPeriod(id: string) {
    setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.filter(p => p.id !== id) }))
  }
  function toggleDeliveryDay(id: string, day: number) {
    setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(p => p.id === id ? { ...p, days: p.days.includes(day) ? p.days.filter(d => d !== day) : [...p.days, day].sort() } : p) }))
  }

  function updateDay(i: number, patch: Partial<DaySlotConfig>) {
    const setter = seasonTab === 'haute' ? setDayMatrixHaute : setDayMatrixBasse
    setter(prev => prev.map((d, idx) => idx === i ? { ...d, ...patch } : d))
  }

  function buildSlots() {
    const slots: any[] = []
    // Auto-detect current season from weekStart prop
    const matrix = (() => {
      if (!weekStart) return dayMatrixBasse
      const [hDebM, hDebJ] = (planningConfig.saison_haute_debut || '06-01').split('-').map(Number)
      const [hFinM, hFinJ] = (planningConfig.saison_haute_fin || '08-31').split('-').map(Number)
      const d = new Date(weekStart)
      const month = d.getMonth() + 1
      const day = d.getDate()
      const afterStart = month > hDebM || (month === hDebM && day >= hDebJ)
      const beforeEnd = month < hFinM || (month === hFinM && day <= hFinJ)
      return (afterStart && beforeEnd) ? dayMatrixHaute : dayMatrixBasse
    })()
    matrix.forEach((d, i) => {
      if (d.morningEnabled) slots.push({ dayOfWeek: i, startTime: morningCfg.start, endTime: morningCfg.end, breakMinutes: morningCfg.breakMinutes, minEmployees: d.morningMin, maxEmployees: d.morningMax })
      if (d.afternoonEnabled) slots.push({ dayOfWeek: i, startTime: afternoonCfg.start, endTime: afternoonCfg.end, breakMinutes: afternoonCfg.breakMinutes, minEmployees: d.afternoonMin, maxEmployees: d.afternoonMax })
    })
    return slots
  }

  const [cycleStartDate, setCycleStartDate] = useState(weekStart || '')
  const [cycleGenerating, setCycleGenerating] = useState(false)

  async function generate() {
    setGenerating(true)
    setResult(null)
    try {
      const data = await onGenerate({ amplitudeMax, restMinHours, priorityMode, respectAvailabilities, weeklyHoursStrict, slots: buildSlots(), excludedEmployeeIds: excludedIds, favoredEmployeeIds: favoredIds, needs, planningConfig, businessRules: rules })
      setResult(data)
    } finally {
      setGenerating(false)
    }
  }

  async function generateCycle() {
    if (buildSlots().length === 0) { alert('Définissez au moins un créneau avant de générer.'); return }
    if (!cycleStartDate) { alert('Sélectionnez une date de début de cycle.'); return }
    setCycleGenerating(true)
    setResult(null)
    try {
      const data = await onCycleGenerate(cycleStartDate, { amplitudeMax, restMinHours, priorityMode, respectAvailabilities, weeklyHoursStrict, slots: buildSlots(), excludedEmployeeIds: excludedIds, favoredEmployeeIds: favoredIds, needs, planningConfig, businessRules: rules })
      setResult(data)
    } catch (err: any) {
      setResult({ error: err?.response?.data?.error || err?.message || 'Erreur inconnue', created: 0 })
    } finally {
      setCycleGenerating(false)
    }
  }

  const inp = 'border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500'
  const btnMode = (active: boolean) => `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`
  const Toggle = ({ value, onChange, label, desc }: { value: boolean; onChange: () => void; label: string; desc: string }) => (
    <label className="flex items-center gap-3 cursor-pointer" onClick={onChange}>
      <div className={`w-10 h-5 rounded-full flex-shrink-0 relative transition-colors ${value ? 'bg-purple-600' : 'bg-gray-300'}`}>
        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-xs text-gray-400">{desc}</p>
      </div>
    </label>
  )

  const activeMatrix = seasonTab === 'haute' ? dayMatrixHaute : dayMatrixBasse

  const SlotPeriodGrid = ({ period, cfg, setCfg, enabledKey, minKey, maxKey }: { period: 'morning' | 'afternoon'; cfg: typeof morningCfg; setCfg: (v: typeof morningCfg) => void; enabledKey: 'morningEnabled' | 'afternoonEnabled'; minKey: 'morningMin' | 'afternoonMin'; maxKey: 'morningMax' | 'afternoonMax' }) => (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {period === 'morning' ? <Sun size={14} className="text-amber-500" /> : <Moon size={14} className="text-orange-400" />}
        <span className="text-sm font-semibold text-gray-800">{period === 'morning' ? 'Créneau Matin' : 'Créneau Après-midi'}</span>
      </div>
      <div className="flex items-center gap-4 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Début</label>
          <input type="time" value={cfg.start} onChange={e => setCfg({ ...cfg, start: e.target.value })} className={inp + ' w-28'} />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Fin</label>
          <input type="time" value={cfg.end} onChange={e => setCfg({ ...cfg, end: e.target.value })} className={inp + ' w-28'} />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Pause (min)</label>
          <input type="number" value={cfg.breakMinutes} onChange={e => setCfg({ ...cfg, breakMinutes: Number(e.target.value) })} min={0} max={60} className={inp + ' w-16'} />
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {SP_DAYS.map((day, i) => {
          const enabled = activeMatrix[i][enabledKey]
          return (
            <div key={i} className="text-center">
              <p className="text-xs text-gray-400 mb-1">{day}</p>
              <button onClick={() => updateDay(i, { [enabledKey]: !enabled } as any)}
                className={`w-full py-1 rounded text-xs font-medium transition-colors ${enabled ? (period === 'morning' ? 'bg-amber-100 text-amber-700 border border-amber-300' : 'bg-orange-100 text-orange-700 border border-orange-300') : 'bg-gray-100 text-gray-400'}`}>
                {enabled ? '✓' : '—'}
              </button>
              {enabled && (
                <div className="mt-1 flex gap-0.5 items-center justify-center">
                  <input type="number" value={activeMatrix[i][minKey]} onChange={e => {
                    const newMin = Math.max(1, Number(e.target.value))
                    const curMax = activeMatrix[i][maxKey]
                    // Si le nouveau min dépasse le max courant, on remonte le max
                    updateDay(i, (newMin > curMax ? { [minKey]: newMin, [maxKey]: newMin } : { [minKey]: newMin }) as any)
                  }} min={1} max={10} title="Min" className="w-7 text-center text-xs border border-gray-200 rounded focus:outline-none py-0.5" />
                  <span className="text-gray-300 text-xs">-</span>
                  <input type="number" value={activeMatrix[i][maxKey]} onChange={e => {
                    const newMax = Math.max(1, Number(e.target.value))
                    const curMin = activeMatrix[i][minKey]
                    // Interdit de saisir un max inférieur au min
                    updateDay(i, { [maxKey]: Math.max(newMax, curMin) } as any)
                  }} min={1} max={10} title="Max" className="w-7 text-center text-xs border border-gray-200 rounded focus:outline-none py-0.5" />
                </div>
              )}
            </div>
          )
        })}
      </div>
      <p className="text-xs text-gray-400 mt-1.5">Min – Max employés par créneau</p>
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-2xl w-full flex flex-col ${tab === 'rules' ? 'max-w-[95vw] max-h-[95vh]' : 'max-w-2xl max-h-[90vh]'}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <Wand2 size={20} className="text-purple-600" />
            <h2 className="font-bold text-gray-900">Smart Planner</h2>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">IA</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveConfig} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${saved2 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {saved2 ? '✓ Sauvegardé' : '💾 Sauvegarder config'}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-1"><X size={20} /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b flex-shrink-0">
          <div className="flex px-3 overflow-x-auto">
            {/* Nouvel onglet Règles (source unique pour génération) */}
            <div className="flex items-end">
              <span className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest pb-2 pr-1 whitespace-nowrap self-end">✨ Nouveau</span>
              <button onClick={() => setTab('rules')}
                className={`px-3 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === 'rules' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                📋 Règles
              </button>
            </div>
            {/* Séparateur */}
            <div className="w-px bg-gray-200 mx-2 my-2" />
            {/* Groupe Commun (algo + IA) */}
            <div className="flex items-end">
              <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest pb-2 pr-1 whitespace-nowrap self-end">Commun</span>
              {(['slots', 'needs', 'employees'] as const).map(key => (
                <button key={key} onClick={() => setTab(key)}
                  className={`px-3 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === key ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-400 hover:text-gray-600'} ${tab !== key ? 'opacity-60' : ''}`} title="Onglet legacy — lu par l'algo uniquement pour rétro-compatibilité">
                  {key === 'slots' ? 'Créneaux' : key === 'needs' ? '🎯 Besoins' : 'Employés'}
                </button>
              ))}
            </div>
            {/* Séparateur */}
            <div className="w-px bg-gray-200 mx-2 my-2" />
            {/* Groupe Paramètres spécifiques */}
            <div className="flex items-end">
              <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest pb-2 pr-1 whitespace-nowrap self-end">Paramètres</span>
              {(['constraints', 'config'] as const).map(key => (
                <button key={key} onClick={() => setTab(key)}
                  className={`px-3 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === key ? (key === 'config' ? 'border-indigo-600 text-indigo-700' : 'border-purple-600 text-purple-700') : 'border-transparent text-gray-400 hover:text-gray-600'} ${tab !== key ? 'opacity-60' : ''}`}>
                  {key === 'constraints' ? '⚙️ Algo' : '🤖 IA'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {tab === 'rules' && (
            <div className="flex gap-4 h-full min-h-[600px]">
              {/* Sidebar sections */}
              <div className="w-56 flex-shrink-0 border-r pr-3 space-y-1">
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-2 pb-2">Sections</div>
                {[
                  { key: 'magasin',   label: '🏪 Magasin',             desc: 'Ouverture, amplitude' },
                  { key: 'saisons',   label: '📅 Saisons',             desc: 'Basse / haute' },
                  { key: 'creneaux',  label: '⏰ Créneaux & effectifs', desc: 'Min/max par jour' },
                  { key: 'livraison', label: '🚚 Jours livraison',     desc: 'Mar/Ven + horaires' },
                  { key: 'humain',    label: '👥 Règles humaines',      desc: 'Saisonnier, Resp, binômes' },
                  { key: 'scoring',   label: '⚖️ Priorités',           desc: 'Mode, tolérance' },
                  { key: 'employes',  label: '👤 Employés',            desc: 'Heures perso, livraison' },
                ].map(s => (
                  <button key={s.key} onClick={() => setRulesSection(s.key as any)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${rulesSection === s.key ? 'bg-emerald-100 text-emerald-800' : 'text-gray-600 hover:bg-gray-100'}`}>
                    <div className="text-sm font-medium">{s.label}</div>
                    <div className="text-[11px] text-gray-400">{s.desc}</div>
                  </button>
                ))}
                <div className="pt-4 mt-4 border-t">
                  <div className="text-[10px] text-gray-400 px-2 leading-relaxed">
                    Cet onglet est la <b>source unique</b> de règles pour la génération auto. Les anciens onglets restent accessibles pour référence mais sont ignorés par l'algo v2.
                  </div>
                </div>
              </div>

              {/* Zone contenu */}
              <div className="flex-1 overflow-y-auto pr-2">
                {/* ─── § 1. MAGASIN ───────────────────────────────── */}
                {rulesSection === 'magasin' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-bold text-gray-800">🏪 Magasin</h3>

                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Horaires d'ouverture par jour</h4>
                      <div className="grid grid-cols-7 gap-2">
                        {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map((d, i) => {
                          const oh = rules.openingHours[i] || { closed: false, open: '08:30', close: '20:00' }
                          return (
                            <div key={i} className="border rounded-lg p-2 text-center">
                              <p className="text-xs font-medium text-gray-600 mb-1">{d}</p>
                              <button onClick={() => setRules(r => ({ ...r, openingHours: r.openingHours.map((x, k) => k === i ? { ...x, closed: !x.closed } : x) }))}
                                className={`w-full text-[10px] py-0.5 rounded mb-1 ${oh.closed ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                {oh.closed ? 'Fermé' : 'Ouvert'}
                              </button>
                              {!oh.closed && (
                                <>
                                  <input type="time" value={oh.open} onChange={e => setRules(r => ({ ...r, openingHours: r.openingHours.map((x, k) => k === i ? { ...x, open: e.target.value } : x) }))} className="w-full text-[10px] border rounded px-1 mb-1" />
                                  <input type="time" value={oh.close} onChange={e => setRules(r => ({ ...r, openingHours: r.openingHours.map((x, k) => k === i ? { ...x, close: e.target.value } : x) }))} className="w-full text-[10px] border rounded px-1" />
                                </>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amplitude max journalière (h)</label>
                        <input type="number" value={(planningConfig as any).amplitude_max_heures ?? 12} onChange={e => updateConfig('amplitude_max_heures', +e.target.value)} min={4} max={24} className="w-24 border rounded px-2 py-1" />
                        <p className="text-xs text-gray-400 mt-1">Durée max entre début et fin d'un shift (pause incluse)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Repos min entre shifts (h)</label>
                        <input type="number" value={(planningConfig as any).repos_min_heures ?? 11} onChange={e => updateConfig('repos_min_heures', +e.target.value)} min={0} max={24} className="w-24 border rounded px-2 py-1" />
                        <p className="text-xs text-gray-400 mt-1">Temps de repos obligatoire entre la fin d'un shift et le début du suivant</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── § 2. SAISONS ───────────────────────────────── */}
                {rulesSection === 'saisons' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-gray-800">📅 Saisons</h3>
                    <p className="text-xs text-gray-500">Définition de la période de haute saison (touristes, événements, été). En dehors → basse saison.</p>
                    <div className="grid grid-cols-2 gap-4 max-w-md">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Début haute saison</label>
                        <input type="text" placeholder="06-01" value={(planningConfig as any).saison_haute_debut ?? '06-01'} onChange={e => updateConfig('saison_haute_debut', e.target.value)} className="w-28 border rounded px-2 py-1" />
                        <p className="text-xs text-gray-400 mt-1">Format MM-JJ</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Fin haute saison</label>
                        <input type="text" placeholder="08-31" value={(planningConfig as any).saison_haute_fin ?? '08-31'} onChange={e => updateConfig('saison_haute_fin', e.target.value)} className="w-28 border rounded px-2 py-1" />
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── § 3. CRÉNEAUX & EFFECTIFS ────────────────── */}
                {rulesSection === 'creneaux' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-bold text-gray-800">⏰ Créneaux & effectifs</h3>

                    {/* Sélecteur saison */}
                    <div className="flex gap-1 bg-gray-50 rounded-lg p-1 w-fit">
                      <button onClick={() => setSeasonTab('basse')} className={`px-4 py-1.5 rounded-md text-sm font-medium ${seasonTab === 'basse' ? 'bg-white shadow text-blue-700' : 'text-gray-500'}`}>🍂 Basse saison</button>
                      <button onClick={() => setSeasonTab('haute')} className={`px-4 py-1.5 rounded-md text-sm font-medium ${seasonTab === 'haute' ? 'bg-white shadow text-orange-600' : 'text-gray-500'}`}>🌞 Haute saison</button>
                    </div>

                    {/* Heures par défaut matin/aprem */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="border rounded-lg p-3">
                        <p className="text-xs font-semibold text-amber-700 mb-2">☀️ Matin (heures par défaut)</p>
                        <div className="flex items-center gap-2">
                          <input type="time" value={morningCfg.start} onChange={e => setMorningCfg({ ...morningCfg, start: e.target.value })} className="border rounded px-2 py-1 text-sm" />
                          <span className="text-gray-400">→</span>
                          <input type="time" value={morningCfg.end} onChange={e => setMorningCfg({ ...morningCfg, end: e.target.value })} className="border rounded px-2 py-1 text-sm" />
                          <span className="text-xs text-gray-500 ml-2">Pause</span>
                          <input type="number" value={morningCfg.breakMinutes} onChange={e => setMorningCfg({ ...morningCfg, breakMinutes: +e.target.value })} min={0} max={60} className="w-16 border rounded px-2 py-1 text-sm" />
                          <span className="text-xs text-gray-400">min</span>
                        </div>
                      </div>
                      <div className="border rounded-lg p-3">
                        <p className="text-xs font-semibold text-orange-700 mb-2">🌙 Après-midi (heures par défaut)</p>
                        <div className="flex items-center gap-2">
                          <input type="time" value={afternoonCfg.start} onChange={e => setAfternoonCfg({ ...afternoonCfg, start: e.target.value })} className="border rounded px-2 py-1 text-sm" />
                          <span className="text-gray-400">→</span>
                          <input type="time" value={afternoonCfg.end} onChange={e => setAfternoonCfg({ ...afternoonCfg, end: e.target.value })} className="border rounded px-2 py-1 text-sm" />
                          <span className="text-xs text-gray-500 ml-2">Pause</span>
                          <input type="number" value={afternoonCfg.breakMinutes} onChange={e => setAfternoonCfg({ ...afternoonCfg, breakMinutes: +e.target.value })} min={0} max={60} className="w-16 border rounded px-2 py-1 text-sm" />
                          <span className="text-xs text-gray-400">min</span>
                        </div>
                      </div>
                    </div>

                    {/* Matrice min/max */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Effectifs min / max par créneau — {seasonTab === 'haute' ? 'Haute' : 'Basse'} saison</h4>
                      <div className="grid grid-cols-7 gap-2">
                        {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map((d, i) => {
                          const matrix = seasonTab === 'haute' ? dayMatrixHaute : dayMatrixBasse
                          const dc = matrix[i]
                          return (
                            <div key={i} className="border rounded-lg p-2">
                              <p className="text-xs font-medium text-gray-600 text-center mb-2">{d}</p>
                              <div className="space-y-2">
                                <div>
                                  <button onClick={() => updateDay(i, { morningEnabled: !dc.morningEnabled })} className={`w-full text-[10px] py-0.5 rounded mb-1 ${dc.morningEnabled ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'}`}>☀️ Matin</button>
                                  {dc.morningEnabled && (
                                    <div className="flex gap-0.5 items-center justify-center">
                                      <input type="number" value={dc.morningMin} onChange={e => { const v = Math.max(1, +e.target.value); updateDay(i, v > dc.morningMax ? { morningMin: v, morningMax: v } : { morningMin: v }) }} min={1} max={10} className="w-7 text-center text-[10px] border rounded py-0.5" />
                                      <span className="text-gray-300 text-[10px]">-</span>
                                      <input type="number" value={dc.morningMax} onChange={e => { const v = Math.max(1, +e.target.value); updateDay(i, { morningMax: Math.max(v, dc.morningMin) }) }} min={1} max={10} className="w-7 text-center text-[10px] border rounded py-0.5" />
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <button onClick={() => updateDay(i, { afternoonEnabled: !dc.afternoonEnabled })} className={`w-full text-[10px] py-0.5 rounded mb-1 ${dc.afternoonEnabled ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}`}>🌙 Aprem</button>
                                  {dc.afternoonEnabled && (
                                    <div className="flex gap-0.5 items-center justify-center">
                                      <input type="number" value={dc.afternoonMin} onChange={e => { const v = Math.max(1, +e.target.value); updateDay(i, v > dc.afternoonMax ? { afternoonMin: v, afternoonMax: v } : { afternoonMin: v }) }} min={1} max={10} className="w-7 text-center text-[10px] border rounded py-0.5" />
                                      <span className="text-gray-300 text-[10px]">-</span>
                                      <input type="number" value={dc.afternoonMax} onChange={e => { const v = Math.max(1, +e.target.value); updateDay(i, { afternoonMax: Math.max(v, dc.afternoonMin) }) }} min={1} max={10} className="w-7 text-center text-[10px] border rounded py-0.5" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── § 4. JOURS LIVRAISON ──────────────────────── */}
                {rulesSection === 'livraison' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-bold text-gray-800">🚚 Jours livraison</h3>

                    {/* Périodes */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Périodes de livraison</h4>
                      <div className="space-y-2">
                        {rules.deliveryPeriods.map(pd => (
                          <div key={pd.id} className="border rounded-lg p-3 bg-gray-50">
                            <div className="flex items-center gap-2 mb-2">
                              <input type="text" value={pd.label} onChange={e => setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(x => x.id === pd.id ? { ...x, label: e.target.value } : x) }))} className="flex-1 border rounded px-2 py-1 text-sm font-medium" placeholder="Libellé période" />
                              <button onClick={() => setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.filter(x => x.id !== pd.id) }))} className="text-xs text-red-600 hover:text-red-800 px-2">🗑</button>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                              <span>Du</span>
                              <input type="number" value={pd.startDay || ''} placeholder="Jour" onChange={e => setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(x => x.id === pd.id ? { ...x, startDay: +e.target.value } : x) }))} min={0} max={31} className="w-14 border rounded px-1 py-0.5" />
                              <input type="number" value={pd.startMonth || ''} placeholder="Mois" onChange={e => setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(x => x.id === pd.id ? { ...x, startMonth: +e.target.value } : x) }))} min={0} max={12} className="w-14 border rounded px-1 py-0.5" />
                              <span>au</span>
                              <input type="number" value={pd.endDay || ''} placeholder="Jour" onChange={e => setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(x => x.id === pd.id ? { ...x, endDay: +e.target.value } : x) }))} min={0} max={31} className="w-14 border rounded px-1 py-0.5" />
                              <input type="number" value={pd.endMonth || ''} placeholder="Mois" onChange={e => setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(x => x.id === pd.id ? { ...x, endMonth: +e.target.value } : x) }))} min={0} max={12} className="w-14 border rounded px-1 py-0.5" />
                              <span className="text-gray-400">(0 = toute l'année)</span>
                            </div>
                            <div className="flex gap-1">
                              {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map((dn, di) => {
                                const active = pd.days.includes(di)
                                return (
                                  <button key={di} onClick={() => setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(x => x.id === pd.id ? { ...x, days: active ? x.days.filter(d => d !== di) : [...x.days, di].sort() } : x) }))} className={`px-2 py-1 text-xs rounded ${active ? 'bg-blue-600 text-white' : 'bg-white border text-gray-500'}`}>{dn}</button>
                                )
                              })}
                            </div>
                          </div>
                        ))}
                        <button onClick={() => setRules(r => ({ ...r, deliveryPeriods: [...r.deliveryPeriods, { id: Date.now().toString(), label: 'Nouvelle période', startMonth: 0, startDay: 0, endMonth: 0, endDay: 0, days: [] }] }))} className="text-xs text-blue-600 hover:text-blue-800">+ Ajouter une période</button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Heure de début matin les jours livraison (défaut global)</label>
                        <input type="time" value={(planningConfig as any).heure_livraison_matin ?? '06:00'} onChange={e => updateConfig('heure_livraison_matin', e.target.value)} className="border rounded px-2 py-1" />
                        <p className="text-xs text-gray-400 mt-1">Chaque employé peut avoir sa propre heure (voir onglet Employés)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Compétence prioritaire jours livraison</label>
                        <input type="text" value={(planningConfig as any).competence_livraison ?? 'Réception livraison'} onChange={e => updateConfig('competence_livraison', e.target.value)} className="w-full border rounded px-2 py-1" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="border rounded p-3">
                        <p className="text-xs font-semibold text-gray-600 mb-2">🍂 Basse saison</p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Min</span>
                          <input type="number" value={(planningConfig as any).livraison_basse_min ?? 4} onChange={e => updateConfig('livraison_basse_min', +e.target.value)} min={1} max={20} className="w-16 border rounded px-2 py-0.5 text-sm" />
                          <span className="text-xs text-gray-500 ml-2">Idéal</span>
                          <input type="number" value={(planningConfig as any).livraison_basse_ideal ?? 5} onChange={e => updateConfig('livraison_basse_ideal', +e.target.value)} min={1} max={20} className="w-16 border rounded px-2 py-0.5 text-sm" />
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-500">Palettes de</span>
                          <input type="number" value={(planningConfig as any).livraison_basse_min_palettes ?? 8} onChange={e => updateConfig('livraison_basse_min_palettes', +e.target.value)} min={1} max={30} className="w-14 border rounded px-1 py-0.5 text-xs" />
                          <span className="text-xs text-gray-500">à</span>
                          <input type="number" value={(planningConfig as any).livraison_basse_max_palettes ?? 10} onChange={e => updateConfig('livraison_basse_max_palettes', +e.target.value)} min={1} max={30} className="w-14 border rounded px-1 py-0.5 text-xs" />
                        </div>
                      </div>
                      <div className="border rounded p-3">
                        <p className="text-xs font-semibold text-gray-600 mb-2">🌞 Haute saison</p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Min</span>
                          <input type="number" value={(planningConfig as any).livraison_haute_min ?? 4} onChange={e => updateConfig('livraison_haute_min', +e.target.value)} min={1} max={20} className="w-16 border rounded px-2 py-0.5 text-sm" />
                          <span className="text-xs text-gray-500 ml-2">Idéal</span>
                          <input type="number" value={(planningConfig as any).livraison_haute_ideal ?? 6} onChange={e => updateConfig('livraison_haute_ideal', +e.target.value)} min={1} max={20} className="w-16 border rounded px-2 py-0.5 text-sm" />
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-500">Palettes max</span>
                          <input type="number" value={(planningConfig as any).livraison_haute_max_palettes ?? 15} onChange={e => updateConfig('livraison_haute_max_palettes', +e.target.value)} min={1} max={50} className="w-14 border rounded px-1 py-0.5 text-xs" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── § 5. RÈGLES HUMAINES ──────────────────────── */}
                {rulesSection === 'humain' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-bold text-gray-800">👥 Règles humaines</h3>

                    <div className="space-y-3">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={(planningConfig as any).resp_prioritaire ?? true} onChange={e => updateConfig('resp_prioritaire', e.target.checked)} className="rounded" />
                        <span className="text-sm">Responsable toujours prioritaire sur son rayon (si dispo)</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={(planningConfig as any).saisonnier_cdi_required ?? true} onChange={e => updateConfig('saisonnier_cdi_required', e.target.checked)} className="rounded" />
                        <span className="text-sm">Un saisonnier doit toujours être accompagné d'au moins 1 CDI/CDD sur le même créneau</span>
                      </label>
                      <div className="ml-6">
                        <label className="text-xs text-gray-600">Min CDI/CDD par créneau avec saisonnier :</label>
                        <input type="number" value={(planningConfig as any).min_cdi_par_saisonnier ?? 1} onChange={e => updateConfig('min_cdi_par_saisonnier', +e.target.value)} min={0} max={5} className="ml-2 w-16 border rounded px-2 py-0.5 text-sm" />
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Règles spéciales dirigeants / famille</h4>
                      <div className="bg-amber-50 border border-amber-200 rounded p-3 space-y-1 text-sm">
                        <p>🚫 <b>Gilbert</b> : jamais planifié par l'algo (à ajouter manuellement)</p>
                        <p>🕒 <b>Manu</b> : planifié uniquement les matins de jours livraison, hors quota</p>
                        <p>👷 <b>Petchara</b> : employée normale, 35h garanties</p>
                        <p className="text-xs text-gray-500 italic pt-2">Ces règles sont codées en dur par nom pour l'instant. Elles pourront être rendues configurables si besoin.</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Binômes (paires qui travaillent ensemble)</h4>
                      <div className="space-y-1">
                        {((planningConfig as any).binomes ?? []).length === 0 ? (
                          <p className="text-xs text-gray-400 italic">Aucun binôme configuré</p>
                        ) : (
                          ((planningConfig as any).binomes as string[][]).map((bin, i) => (
                            <div key={i} className="text-sm flex items-center gap-2">
                              <span className="bg-blue-50 px-2 py-1 rounded">{bin[0]} ↔ {bin[1]}</span>
                              <button onClick={() => updateConfig('binomes', ((planningConfig as any).binomes as string[][]).filter((_, k) => k !== i))} className="text-xs text-red-600">🗑</button>
                            </div>
                          ))
                        )}
                        <p className="text-xs text-gray-400 italic mt-2">Ajout de binôme via l'ancien onglet Algo → Contraintes humaines (en attendant UI dédiée ici)</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── § 6. PRIORITÉS ─────────────────────────────── */}
                {rulesSection === 'scoring' && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-bold text-gray-800">⚖️ Priorités</h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mode de priorité</label>
                      <div className="flex gap-2">
                        {[
                          { k: 'equity', l: '⚖️ Équité (équilibrer les heures)' },
                          { k: 'cost',   l: '💰 Coût (privilégier les moins chers)' },
                          { k: 'coverage', l: '🛡️ Couverture (remplir au max)' },
                        ].map(({ k, l }) => (
                          <button key={k} onClick={() => updateConfig('priorityMode', k)} className={`px-3 py-2 rounded-lg text-sm ${((planningConfig as any).priorityMode ?? 'equity') === k ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{l}</button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tolérance heures contrat (h)</label>
                        <input type="number" step="0.25" value={(planningConfig as any).tolerance_heures ?? 0.5} onChange={e => updateConfig('tolerance_heures', +e.target.value)} min={0} max={5} className="w-24 border rounded px-2 py-1" />
                        <p className="text-xs text-gray-400 mt-1">Marge autorisée par rapport au contrat (± cette valeur)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Écart max matin/aprem (personnes)</label>
                        <input type="number" value={(planningConfig as any).ecart_max_matin_aprem ?? 1} onChange={e => updateConfig('ecart_max_matin_aprem', +e.target.value)} min={0} max={5} className="w-24 border rounded px-2 py-1" />
                        <p className="text-xs text-gray-400 mt-1">Différence max entre effectif matin et effectif aprem (warning uniquement)</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={weeklyHoursStrict} onChange={e => setWeeklyHoursStrict(e.target.checked)} className="rounded" />
                        <span className="text-sm"><b>Heures contrat sacrées</b> — ne jamais dépasser le contrat (+ tolérance ci-dessus). Fortement recommandé.</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={respectAvailabilities} onChange={e => setRespectAvailabilities(e.target.checked)} className="rounded" />
                        <span className="text-sm">Respecter les disponibilités saisies (matin = matin, aprem = aprem)</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={(planningConfig as any).pause_incluse ?? true} onChange={e => updateConfig('pause_incluse', e.target.checked)} className="rounded" />
                        <span className="text-sm">Pause incluse dans le temps de travail (pas soustraite)</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* ─── § 7. EMPLOYÉS ───────────────────────────── */}
                {rulesSection === 'employes' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-gray-800">👤 Employés — heures perso & exclusions</h3>
                    <p className="text-xs text-gray-500">Fiche récapitulative pour cette génération. Heures perso matin/aprem et heure début jour livraison seront éditables ici dès que les champs BDD seront créés.</p>

                    <div className="space-y-2">
                      {employees.filter(e => e.status !== 'archived').map(emp => {
                        const isExcluded = excludedIds.includes(emp.id)
                        const isFavored = favoredIds.includes(emp.id)
                        return (
                          <div key={emp.id} className={`border rounded-lg p-3 flex items-center gap-3 ${isExcluded ? 'bg-red-50 border-red-200' : isFavored ? 'bg-emerald-50 border-emerald-200' : 'bg-white'}`}>
                            <div className="flex-1">
                              <div className="text-sm font-medium">{emp.firstName} {emp.lastName || ''}</div>
                              <div className="text-xs text-gray-500">{emp.contractType} · {emp.weeklyHours}h/sem · {emp.position?.name || 'sans poste'}</div>
                            </div>
                            <div className="flex gap-1">
                              <button onClick={() => { setFavoredIds(ids => ids.filter(x => x !== emp.id)); setExcludedIds(ids => isExcluded ? ids.filter(x => x !== emp.id) : [...ids, emp.id]) }} className={`text-xs px-2 py-1 rounded ${isExcluded ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-red-100'}`}>Exclu</button>
                              <button onClick={() => { setExcludedIds(ids => ids.filter(x => x !== emp.id)); setFavoredIds(ids => isFavored ? ids.filter(x => x !== emp.id) : [...ids, emp.id]) }} className={`text-xs px-2 py-1 rounded ${isFavored ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-emerald-100'}`}>Favori</button>
                              <button onClick={() => { setExcludedIds(ids => ids.filter(x => x !== emp.id)); setFavoredIds(ids => ids.filter(x => x !== emp.id)) }} className={`text-xs px-2 py-1 rounded ${!isExcluded && !isFavored ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>Normal</button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'slots' && (
            <div className="space-y-4">
              {/* Sous-onglets saison */}
              <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1 w-fit">
                <button onClick={() => setSeasonTab('basse')}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${seasonTab === 'basse' ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
                  🍂 Basse saison
                </button>
                <button onClick={() => setSeasonTab('haute')}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${seasonTab === 'haute' ? 'bg-white shadow text-orange-600' : 'text-gray-500 hover:text-gray-700'}`}>
                  🌞 Haute saison
                </button>
              </div>
              <p className="text-xs text-gray-400 -mt-1">
                {seasonTab === 'haute'
                  ? `Haute saison : ${planningConfig.saison_haute_debut} → ${planningConfig.saison_haute_fin} (configuré dans 🤖 IA)`
                  : `Basse saison : reste de l'année`}
              </p>
              <SlotPeriodGrid period="morning" cfg={morningCfg} setCfg={setMorningCfg} enabledKey="morningEnabled" minKey="morningMin" maxKey="morningMax" />
              <hr className="border-gray-100" />
              <SlotPeriodGrid period="afternoon" cfg={afternoonCfg} setCfg={setAfternoonCfg} enabledKey="afternoonEnabled" minKey="afternoonMin" maxKey="afternoonMax" />
            </div>
          )}

          {tab === 'constraints' && (() => {
            const DAY_SHORT_CFG2 = ['L','M','Me','J','V','S','D']
            const NumInput2 = ({ label, cfgKey, min, max, step }: { label: string; cfgKey: string; min?: number; max?: number; step?: number }) => (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-gray-700 flex-1">{label}</span>
                <input type="number" min={min} max={max} step={step ?? 1}
                  value={(planningConfig as any)[cfgKey]}
                  onChange={e => updateConfig(cfgKey, parseFloat(e.target.value) || 0)}
                  className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-center outline-none focus:border-purple-400" />
              </div>
            )
            const ToggleCfg2 = ({ label, desc, cfgKey }: { label: string; desc?: string; cfgKey: string }) => (
              <label className="flex items-center gap-3 cursor-pointer" onClick={() => updateConfig(cfgKey, !(planningConfig as any)[cfgKey])}>
                <div className={`w-10 h-5 rounded-full flex-shrink-0 relative transition-colors ${(planningConfig as any)[cfgKey] ? 'bg-purple-600' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${(planningConfig as any)[cfgKey] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{label}</p>
                  {desc && <p className="text-xs text-gray-400">{desc}</p>}
                </div>
              </label>
            )
            const DeliveryDays2 = ({ label, cfgKey }: { label: string; cfgKey: 'livraison_basse_jours' | 'livraison_haute_jours' }) => (
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1.5">{label}</p>
                <div className="flex gap-1.5">
                  {DAY_SHORT_CFG2.map((d, i) => {
                    const dayVal = i + 1
                    const active = ((planningConfig[cfgKey] as number[]) || []).includes(dayVal)
                    return (
                      <button key={i} onClick={() => toggleLivraisonJour(cfgKey, dayVal)}
                        className={`w-8 h-8 text-xs font-medium rounded-full transition-colors ${active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        {d}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
            const DAY_LABELS_FULL2 = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche']
            const DAY_SHORT_R2 = ['L','M','Me','J','V','S','D']
            const MONTHS_R2 = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']
            const updateOpeningDayA = (i: number, patch: Partial<OpeningDay>) =>
              setRules(r => ({ ...r, openingHours: r.openingHours.map((d, idx) => idx === i ? { ...d, ...patch } : d) }))
            const updateDeliveryPeriodA = (id: string, patch: Partial<DeliveryPeriod>) =>
              setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(p => p.id === id ? { ...p, ...patch } : p) }))
            const addDeliveryPeriodA = () =>
              setRules(r => ({ ...r, deliveryPeriods: [...r.deliveryPeriods, { id: Date.now().toString(), label: 'Nouvelle période', startMonth: 0, startDay: 0, endMonth: 0, endDay: 0, days: [] }] }))
            const removeDeliveryPeriodA = (id: string) =>
              setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.filter(p => p.id !== id) }))
            const toggleDeliveryDayA = (id: string, day: number) =>
              setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(p => p.id === id ? { ...p, days: p.days.includes(day) ? p.days.filter(d => d !== day) : [...p.days, day].sort() } : p) }))

            return (
            <div className="space-y-6">
              {/* Paramètres Algo existants */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Amplitude journalière max</label>
                <div className="flex gap-2">
                  {[8, 10, 11, 12].map(h => <button key={h} onClick={() => setAmplitudeMax(h)} className={btnMode(amplitudeMax === h)}>{h}h</button>)}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Repos minimum entre shifts</label>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setRestMinHours(0)} className={btnMode(restMinHours === 0)}>Désactivé</button>
                  {[9, 10, 11, 12].map(h => <button key={h} onClick={() => setRestMinHours(h)} className={btnMode(restMinHours === h)}>{h}h</button>)}
                </div>
                <p className="text-xs text-gray-400 mt-1">11h = seuil légal recommandé · Désactivé = aucune contrainte</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Mode de priorité</label>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setPriorityMode('equity')} className={btnMode(priorityMode === 'equity')}>Équité (heures contrat)</button>
                  <button onClick={() => setPriorityMode('cost')} className={btnMode(priorityMode === 'cost')}>Minimiser coût</button>
                  <button onClick={() => setPriorityMode('coverage')} className={btnMode(priorityMode === 'coverage')}>Couverture max</button>
                </div>
              </div>
              <div className="space-y-4">
                <Toggle value={respectAvailabilities} onChange={() => setRespectAvailabilities(v => !v)} label="Respecter les disponibilités" desc="Ne planifie pas en dehors des créneaux définis par le salarié" />
                <Toggle value={weeklyHoursStrict} onChange={() => setWeeklyHoursStrict(v => !v)} label="Heures contractuelles strictes" desc="Ne dépasse pas les heures hebdomadaires contractuelles" />
              </div>

              {/* ── Paramètres partagés avec l'IA ── */}
              <div className="border-t pt-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Paramètres avancés (partagés avec IA)</p>
              </div>

              {/* Règles Magasin */}
              <div className="border border-indigo-200 rounded-xl p-4 space-y-4 bg-indigo-50/30">
                <p className="text-sm font-semibold text-indigo-800">📋 Règles Magasin</p>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">🕐 Horaires d'ouverture</p>
                  <div className="space-y-1">
                    {rules.openingHours.map((day, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-16 font-medium">{DAY_LABELS_FULL2[i]}</span>
                        <label className="flex items-center gap-1 text-xs text-gray-500 w-16">
                          <input type="checkbox" checked={day.closed} onChange={e => updateOpeningDayA(i, { closed: e.target.checked })} className="rounded" />
                          Fermé
                        </label>
                        {!day.closed && (
                          <>
                            <input type="time" value={day.open} onChange={e => updateOpeningDayA(i, { open: e.target.value })} className="text-xs border border-gray-200 rounded px-1.5 py-0.5 outline-none focus:border-indigo-400 w-24 bg-white" />
                            <span className="text-gray-400 text-xs">–</span>
                            <input type="time" value={day.close} onChange={e => updateOpeningDayA(i, { close: e.target.value })} className="text-xs border border-gray-200 rounded px-1.5 py-0.5 outline-none focus:border-indigo-400 w-24 bg-white" />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-600">🚚 Jours de livraison</p>
                    <button onClick={addDeliveryPeriodA} className="flex items-center gap-1 text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 font-medium"><Plus size={11} /> Période</button>
                  </div>
                  <div className="space-y-2">
                    {rules.deliveryPeriods.map(period => (
                      <div key={period.id} className="border border-gray-200 rounded-lg p-2 space-y-1.5 bg-white">
                        <div className="flex items-center gap-2">
                          <input value={period.label} onChange={e => updateDeliveryPeriodA(period.id, { label: e.target.value })} className="flex-1 text-xs font-medium border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400" />
                          <button onClick={() => removeDeliveryPeriodA(period.id)} className="text-gray-300 hover:text-red-400"><Trash2 size={13} /></button>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-600 flex-wrap">
                          <span className="text-gray-400">Du</span>
                          <select value={period.startDay} onChange={e => updateDeliveryPeriodA(period.id, { startDay: +e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none">
                            {period.startDay === 0 && <option value={0}>—</option>}
                            {Array.from({length:31},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                          </select>
                          <select value={period.startMonth} onChange={e => updateDeliveryPeriodA(period.id, { startMonth: +e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none">
                            {period.startMonth === 0 && <option value={0}>—</option>}
                            {MONTHS_R2.map((m,mi)=><option key={mi+1} value={mi+1}>{m}</option>)}
                          </select>
                          <span className="text-gray-400">au</span>
                          <select value={period.endDay} onChange={e => updateDeliveryPeriodA(period.id, { endDay: +e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none">
                            {period.endDay === 0 && <option value={0}>—</option>}
                            {Array.from({length:31},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                          </select>
                          <select value={period.endMonth} onChange={e => updateDeliveryPeriodA(period.id, { endMonth: +e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none">
                            {period.endMonth === 0 && <option value={0}>—</option>}
                            {MONTHS_R2.map((m,mi)=><option key={mi+1} value={mi+1}>{m}</option>)}
                          </select>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          {DAY_SHORT_R2.map((d, di) => (
                            <button key={di} onClick={() => toggleDeliveryDayA(period.id, di)} className={`w-7 h-7 text-xs font-medium rounded-full transition-colors ${period.days.includes(di) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>{d}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Saison */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800">🌞 Saison haute</p>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 w-10">Du</span>
                  <input type="text" placeholder="06-01" value={planningConfig.saison_haute_debut}
                    onChange={e => updateConfig('saison_haute_debut', e.target.value)}
                    className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-center outline-none focus:border-purple-400" />
                  <span className="text-sm text-gray-600">au</span>
                  <input type="text" placeholder="08-31" value={planningConfig.saison_haute_fin}
                    onChange={e => updateConfig('saison_haute_fin', e.target.value)}
                    className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-center outline-none focus:border-purple-400" />
                  <span className="text-xs text-gray-400">(MM-JJ)</span>
                </div>
              </div>

              {/* Livraison basse */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800">🚚 Livraison — Basse saison</p>
                <DeliveryDays2 label="Jours de livraison" cfgKey="livraison_basse_jours" />
                <div className="grid grid-cols-2 gap-3">
                  <NumInput2 label="Minimum staff" cfgKey="livraison_basse_min" min={1} max={20} />
                  <NumInput2 label="Idéal staff" cfgKey="livraison_basse_ideal" min={1} max={20} />
                </div>
              </div>

              {/* Livraison haute */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800">🚚 Livraison — Haute saison</p>
                <DeliveryDays2 label="Jours de livraison" cfgKey="livraison_haute_jours" />
                <div className="grid grid-cols-2 gap-3">
                  <NumInput2 label="Minimum staff" cfgKey="livraison_haute_min" min={1} max={20} />
                  <NumInput2 label="Idéal staff" cfgKey="livraison_haute_ideal" min={1} max={20} />
                </div>
              </div>

              {/* Réglementation */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-800">⚖️ Réglementation</p>
                <NumInput2 label="Repos minimum entre shifts (h)" cfgKey="repos_min_heures" min={0} max={24} />
                <NumInput2 label="Amplitude max journalière (h)" cfgKey="amplitude_max_heures" min={4} max={24} />
                <NumInput2 label="Tolérance heures contrat (h)" cfgKey="tolerance_heures" min={0} max={2} step={0.25} />
                <ToggleCfg2 label="Pause 20 min incluse dans le temps de travail" desc="Convention collective : les heures sont comptées en brut" cfgKey="pause_incluse" />
              </div>

              {/* Contraintes humaines */}
              <div className="border border-gray-200 rounded-xl p-4 space-y-4">
                <p className="text-sm font-semibold text-gray-800">👥 Contraintes humaines</p>
                <ToggleCfg2 label="Resp prioritaire sur non-Resp" desc="Ex: Resp Frais prioritaire sur Frais si dispo" cfgKey="resp_prioritaire" />
                <ToggleCfg2 label="Saisonnier toujours avec un CDI/CDD" desc="Un employé saisonnier ne peut pas être seul sur une plage horaire" cfgKey="saisonnier_cdi_required" />
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Binômes (toujours planifiés ensemble)</p>
                  <div className="space-y-1.5 mb-2">
                    {(planningConfig.binomes || []).map((binome: string[], bi: number) => (
                      <div key={bi} className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-1.5">
                        <span className="flex-1 text-sm text-blue-800">{binome.join(' + ')}</span>
                        <button onClick={() => updateConfigAndSave('binomes', (planningConfig.binomes || []).filter((_: any, i: number) => i !== bi))}
                          className="text-gray-300 hover:text-red-400"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <select value={binomeEmp1} onChange={e => setBinomeEmp1(e.target.value)}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-purple-400">
                      <option value="">Employé 1</option>
                      {employees.filter(e => e.firstName !== binomeEmp2).map(e => (
                        <option key={e.id} value={e.firstName}>{e.firstName} {e.lastName}</option>
                      ))}
                    </select>
                    <select value={binomeEmp2} onChange={e => setBinomeEmp2(e.target.value)}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-purple-400">
                      <option value="">Employé 2</option>
                      {employees.filter(e => e.firstName !== binomeEmp1).map(e => (
                        <option key={e.id} value={e.firstName}>{e.firstName} {e.lastName}</option>
                      ))}
                    </select>
                    <button onClick={() => {
                      if (binomeEmp1 && binomeEmp2 && binomeEmp1 !== binomeEmp2) {
                        updateConfigAndSave('binomes', [...(planningConfig.binomes || []), [binomeEmp1, binomeEmp2]])
                        setBinomeEmp1(''); setBinomeEmp2('')
                      }
                    }} className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 font-medium"><Plus size={13} /></button>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Groupes liés (même shift obligatoire)</p>
                  <div className="space-y-1.5 mb-2">
                    {(planningConfig.groupes_lies || []).map((groupe, gi) => (
                      <div key={gi} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                        <span className="flex-1 text-sm text-gray-700">{groupe.join(' + ')}</span>
                        <button onClick={() => updateConfig('groupes_lies', (planningConfig.groupes_lies || []).filter((_: any, i: number) => i !== gi))}
                          className="text-gray-300 hover:text-red-400"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={newGroupInput} onChange={e => setNewGroupInput(e.target.value)}
                      placeholder="Prénom1, Prénom2"
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-purple-400" />
                    <button onClick={() => {
                      const noms = newGroupInput.split(',').map(s => s.trim()).filter(Boolean)
                      if (noms.length >= 2) { updateConfig('groupes_lies', [...(planningConfig.groupes_lies || []), noms]); setNewGroupInput('') }
                    }} className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 font-medium"><Plus size={13} /></button>
                  </div>
                </div>
              </div>

              {/* Règles opérationnelles */}
              <div className="border border-emerald-200 bg-emerald-50/30 rounded-xl p-4 space-y-4">
                <p className="text-sm font-semibold text-emerald-900">⚙️ Règles opérationnelles</p>
                <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-3">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Couverture des créneaux</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Min. employés/créneau</label>
                      <input type="number" min={1} max={10} value={(planningConfig as any).min_employes_creneau ?? 2}
                        onChange={e => updateConfig('min_employes_creneau', parseInt(e.target.value) || 2)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Écart max matin/AM</label>
                      <input type="number" min={0} max={5} value={(planningConfig as any).ecart_max_matin_aprem ?? 1}
                        onChange={e => updateConfig('ecart_max_matin_aprem', parseInt(e.target.value) || 0)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-3">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Horaires magasin</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Arrivée préparation</label>
                      <input type="time" value={(planningConfig as any).heure_arrivee_preparation ?? '07:00'}
                        onChange={e => updateConfig('heure_arrivee_preparation', e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Départ fermeture</label>
                      <input type="time" value={(planningConfig as any).heure_depart_fermeture ?? '20:15'}
                        onChange={e => updateConfig('heure_depart_fermeture', e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-3">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Livraisons</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Heure début livraison</label>
                      <input type="time" value={(planningConfig as any).heure_livraison_matin ?? '06:00'}
                        onChange={e => updateConfig('heure_livraison_matin', e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Compétence livraison</label>
                      <input type="text" value={(planningConfig as any).competence_livraison ?? 'Réception livraison'}
                        onChange={e => updateConfig('competence_livraison', e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Basse saison min palettes</label>
                      <input type="number" min={1} max={30} value={(planningConfig as any).livraison_basse_min_palettes ?? 8}
                        onChange={e => updateConfig('livraison_basse_min_palettes', parseInt(e.target.value) || 8)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Basse saison max palettes</label>
                      <input type="number" min={1} max={30} value={(planningConfig as any).livraison_basse_max_palettes ?? 10}
                        onChange={e => updateConfig('livraison_basse_max_palettes', parseInt(e.target.value) || 10)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Haute saison max palettes</label>
                      <input type="number" min={1} max={50} value={(planningConfig as any).livraison_haute_max_palettes ?? 15}
                        onChange={e => updateConfig('livraison_haute_max_palettes', parseInt(e.target.value) || 15)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Saisonniers</p>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">CDI/CDD min. par créneau avec saisonnier</label>
                    <input type="number" min={0} max={5} value={(planningConfig as any).min_cdi_par_saisonnier ?? 1}
                      onChange={e => updateConfig('min_cdi_par_saisonnier', parseInt(e.target.value) || 1)}
                      className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                  </div>
                </div>
              </div>

              {/* Sauvegarder */}
              <button onClick={() => { saveConfigToBackend(); saveConfig() }} disabled={configSaving}
                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors ${configSaved ? 'bg-green-100 text-green-700' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                {configSaving ? 'Sauvegarde...' : configSaved ? '✓ Paramètres sauvegardés' : '💾 Sauvegarder les paramètres'}
              </button>
            </div>
            )
          })()}


          {tab === 'needs' && (() => {
            const DAY_LABELS = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche']
            const periodColor = (p: string) => p === 'morning' ? 'bg-amber-100 text-amber-700' : p === 'afternoon' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700'
            return (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">Définissez les postes <strong>obligatoires</strong> par jour et par période. UtilIA les respectera en priorité absolue.</p>
                {DAY_LABELS.map((dayLabel, dayIdx) => {
                  const dayNeeds = needs[dayIdx] || []
                  return (
                    <div key={dayIdx} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                        <span className="text-sm font-semibold text-gray-700">{dayLabel}</span>
                        <button type="button" onClick={() => setNeeds(prev => ({ ...prev, [dayIdx]: [...(prev[dayIdx]||[]), { skill: '', count: 1, period: 'morning' }] }))}
                          className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium">
                          <Plus size={12} /> Ajouter
                        </button>
                      </div>
                      {dayNeeds.length === 0 ? (
                        <p className="text-xs text-gray-400 italic px-3 py-2">Aucun besoin — UtilIA décide librement</p>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {dayNeeds.map((need, ni) => (
                            <div key={ni} className="flex items-center gap-2 px-3 py-1.5">
                              <select value={need.skill}
                                onChange={e => setNeeds(prev => { const d=[...(prev[dayIdx]||[])]; d[ni]={...d[ni],skill:e.target.value}; return {...prev,[dayIdx]:d} })}
                                className="flex-1 text-sm border border-gray-200 rounded px-2 py-1 outline-none focus:border-purple-400 bg-white">
                                <option value="">— Choisir un poste —</option>
                                {positions.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                              </select>
                              <input type="number" min={1} max={10} value={need.count}
                                onChange={e => setNeeds(prev => { const d=[...(prev[dayIdx]||[])]; d[ni]={...d[ni],count:parseInt(e.target.value)||1}; return {...prev,[dayIdx]:d} })}
                                className="w-14 text-sm border border-gray-200 rounded px-2 py-1 outline-none focus:border-purple-400 text-center" />
                              <select value={need.period}
                                onChange={e => setNeeds(prev => { const d=[...(prev[dayIdx]||[])]; d[ni]={...d[ni],period:e.target.value as any}; return {...prev,[dayIdx]:d} })}
                                className={`text-xs rounded px-2 py-1 border-0 font-medium cursor-pointer ${periodColor(need.period)}`}>
                                <option value="morning">Matin</option>
                                <option value="afternoon">A-M</option>
                                <option value="both">Journée</option>
                              </select>
                              <button type="button" onClick={() => setNeeds(prev => { const d=(prev[dayIdx]||[]).filter((_,i)=>i!==ni); return {...prev,[dayIdx]:d} })}
                                className="text-gray-300 hover:text-red-500 flex-shrink-0"><X size={14} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })()}

          {tab === 'employees' && (
            <div>
              <p className="text-sm text-gray-500 mb-4">Configurez l'inclusion de chaque employé dans la génération.</p>
              <div className="space-y-1">
                {employees.map(emp => {
                  const isExcluded = excludedIds.includes(emp.id)
                  const isFavored = favoredIds.includes(emp.id)
                  return (
                    <div key={emp.id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {emp.firstName[0]}{emp.lastName[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{emp.firstName} {emp.lastName}</p>
                        <p className="text-xs text-gray-400">{emp.weeklyHours}h/sem.</p>
                      </div>
                      <div className="flex gap-1">
                        {[
                          { label: 'Normal', active: !isExcluded && !isFavored, cls: 'bg-gray-200 text-gray-700', action: () => { setExcludedIds(p => p.filter(id => id !== emp.id)); setFavoredIds(p => p.filter(id => id !== emp.id)) } },
                          { label: 'Favorisé', active: isFavored, cls: 'bg-green-100 text-green-700', action: () => { setFavoredIds(p => p.includes(emp.id) ? p.filter(id => id !== emp.id) : [...p, emp.id]); setExcludedIds(p => p.filter(id => id !== emp.id)) } },
                          { label: 'Exclu', active: isExcluded, cls: 'bg-red-100 text-red-700', action: () => { setExcludedIds(p => p.includes(emp.id) ? p.filter(id => id !== emp.id) : [...p, emp.id]); setFavoredIds(p => p.filter(id => id !== emp.id)) } },
                        ].map(({ label, active, cls, action }) => (
                          <button key={label} onClick={action}
                            className={`px-2 py-1 text-xs rounded-lg font-medium transition-colors ${active ? cls : 'text-gray-400 hover:bg-gray-100'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {false && (() => {
            const DAY_LABELS_FULL = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche']
            const DAY_SHORT = ['L','M','Me','J','V','S','D']
            const MONTHS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']
            return (
              <div className="space-y-6">
                {/* Horaires d'ouverture */}
                <div>
                  <p className="text-sm font-semibold text-gray-800 mb-3">🕐 Horaires d'ouverture</p>
                  <div className="space-y-1.5">
                    {rules.openingHours.map((day, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-16 font-medium">{DAY_LABELS_FULL[i]}</span>
                        <label className="flex items-center gap-1 text-xs text-gray-500 w-16">
                          <input type="checkbox" checked={day.closed}
                            onChange={e => updateOpeningDay(i, { closed: e.target.checked })}
                            className="rounded" />
                          Fermé
                        </label>
                        {!day.closed && (
                          <>
                            <input type="time" value={day.open}
                              onChange={e => updateOpeningDay(i, { open: e.target.value })}
                              className="text-xs border border-gray-200 rounded px-1.5 py-0.5 outline-none focus:border-purple-400 w-24" />
                            <span className="text-gray-400 text-xs">–</span>
                            <input type="time" value={day.close}
                              onChange={e => updateOpeningDay(i, { close: e.target.value })}
                              className="text-xs border border-gray-200 rounded px-1.5 py-0.5 outline-none focus:border-purple-400 w-24" />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Jours de livraison */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-800">🚚 Jours de livraison</p>
                    <button onClick={addDeliveryPeriod}
                      className="flex items-center gap-1 text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium">
                      <Plus size={12} /> Période
                    </button>
                  </div>
                  <div className="space-y-3">
                    {rules.deliveryPeriods.map(period => (
                      <div key={period.id} className="border border-gray-200 rounded-xl p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <input value={period.label} onChange={e => updateDeliveryPeriod(period.id, { label: e.target.value })}
                            className="flex-1 text-xs font-medium border border-gray-200 rounded px-2 py-1 outline-none focus:border-purple-400" />
                          <button onClick={() => removeDeliveryPeriod(period.id)}
                            className="text-gray-300 hover:text-red-400 flex-shrink-0"><Trash2 size={14} /></button>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600 flex-wrap">
                          <span className="text-gray-400">Du</span>
                          <select value={period.startDay} onChange={e => updateDeliveryPeriod(period.id, { startDay: +e.target.value })}
                            className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none focus:border-purple-400">
                            {period.startDay === 0 && <option value={0}>—</option>}
                            {Array.from({length:31},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                          </select>
                          <select value={period.startMonth} onChange={e => updateDeliveryPeriod(period.id, { startMonth: +e.target.value })}
                            className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none focus:border-purple-400">
                            {period.startMonth === 0 && <option value={0}>—</option>}
                            {MONTHS.map((m,mi)=><option key={mi+1} value={mi+1}>{m}</option>)}
                          </select>
                          <span className="text-gray-400">au</span>
                          <select value={period.endDay} onChange={e => updateDeliveryPeriod(period.id, { endDay: +e.target.value })}
                            className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none focus:border-purple-400">
                            {period.endDay === 0 && <option value={0}>—</option>}
                            {Array.from({length:31},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                          </select>
                          <select value={period.endMonth} onChange={e => updateDeliveryPeriod(period.id, { endMonth: +e.target.value })}
                            className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none focus:border-purple-400">
                            {period.endMonth === 0 && <option value={0}>—</option>}
                            {MONTHS.map((m,mi)=><option key={mi+1} value={mi+1}>{m}</option>)}
                          </select>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          {DAY_SHORT.map((d, di) => (
                            <button key={di} onClick={() => toggleDeliveryDay(period.id, di)}
                              className={`w-8 h-8 text-xs font-medium rounded-full transition-colors ${period.days.includes(di) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                              {d}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">💡 Si aucune date n'est définie, la période s'applique toute l'année comme règle par défaut.</p>
                </div>

                <div className="bg-blue-50 rounded-xl p-3 text-xs text-blue-700">
                  <p className="font-medium mb-1">UtilIA utilise ces règles</p>
                  <p>Les horaires d'ouverture et les jours de livraison sont transmis à UtilIA lors de la génération du planning. Clique sur 💾 Sauvegarder config pour mémoriser tes réglages.</p>
                </div>
              </div>
            )
          })()}

          {tab === 'config' && (() => {
            const DAY_SHORT_CFG = ['L','M','Me','J','V','S','D']
            const dayNum = [1,2,3,4,5,6,7] // 1=Lun…7=Dim
            const NumInput = ({ label, cfgKey, min, max, step }: { label: string; cfgKey: string; min?: number; max?: number; step?: number }) => (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-gray-700 flex-1">{label}</span>
                <input type="number" min={min} max={max} step={step ?? 1}
                  value={(planningConfig as any)[cfgKey]}
                  onChange={e => updateConfig(cfgKey, parseFloat(e.target.value) || 0)}
                  className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-center outline-none focus:border-purple-400" />
              </div>
            )
            const ToggleCfg = ({ label, desc, cfgKey }: { label: string; desc?: string; cfgKey: string }) => (
              <label className="flex items-center gap-3 cursor-pointer" onClick={() => updateConfig(cfgKey, !(planningConfig as any)[cfgKey])}>
                <div className={`w-10 h-5 rounded-full flex-shrink-0 relative transition-colors ${(planningConfig as any)[cfgKey] ? 'bg-purple-600' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${(planningConfig as any)[cfgKey] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{label}</p>
                  {desc && <p className="text-xs text-gray-400">{desc}</p>}
                </div>
              </label>
            )
            const DeliveryDays = ({ label, cfgKey }: { label: string; cfgKey: 'livraison_basse_jours' | 'livraison_haute_jours' }) => (
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1.5">{label}</p>
                <div className="flex gap-1.5">
                  {DAY_SHORT_CFG.map((d, i) => {
                    const dayVal = i + 1
                    const active = ((planningConfig[cfgKey] as number[]) || []).includes(dayVal)
                    return (
                      <button key={i} onClick={() => toggleLivraisonJour(cfgKey, dayVal)}
                        className={`w-8 h-8 text-xs font-medium rounded-full transition-colors ${active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        {d}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
            const DAY_LABELS_FULL = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche']
            const DAY_SHORT_R = ['L','M','Me','J','V','S','D']
            const MONTHS_R = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']
            const updateOpeningDayC = (i: number, patch: Partial<OpeningDay>) =>
              setRules(r => ({ ...r, openingHours: r.openingHours.map((d, idx) => idx === i ? { ...d, ...patch } : d) }))
            const updateDeliveryPeriodC = (id: string, patch: Partial<DeliveryPeriod>) =>
              setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(p => p.id === id ? { ...p, ...patch } : p) }))
            const addDeliveryPeriodC = () =>
              setRules(r => ({ ...r, deliveryPeriods: [...r.deliveryPeriods, { id: Date.now().toString(), label: 'Nouvelle période', startMonth: 0, startDay: 0, endMonth: 0, endDay: 0, days: [] }] }))
            const removeDeliveryPeriodC = (id: string) =>
              setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.filter(p => p.id !== id) }))
            const toggleDeliveryDayC = (id: string, day: number) =>
              setRules(r => ({ ...r, deliveryPeriods: r.deliveryPeriods.map(p => p.id === id ? { ...p, days: p.days.includes(day) ? p.days.filter(d => d !== day) : [...p.days, day].sort() } : p) }))

            if (configLoading) return <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Chargement...</div>
            const DAY_SHORT_WC = ['L','M','Me','J','V','S','D']
            return (
              <div className="space-y-6">

                {/* Contexte semaine (calculé automatiquement) */}
                <div className="border border-emerald-200 rounded-xl p-4 space-y-3 bg-emerald-50/30">
                  <p className="text-sm font-semibold text-emerald-800">📊 Contexte semaine (auto)</p>

                  {/* Météo + CA N-1 */}
                  {weekContext.meteo.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1.5">🌤️ Météo prévision + CA N-1</p>
                      <div className="grid grid-cols-7 gap-1">
                        {DAY_SHORT_WC.map((d, i) => {
                          const m = weekContext.meteo[i]
                          return (
                            <div key={i} className="text-center bg-white rounded-lg py-1.5 px-0.5 border border-gray-100">
                              <p className="text-[9px] text-gray-400 font-medium mb-0.5">{d}</p>
                              <p className="text-base leading-none">{m?.icon ?? '—'}</p>
                              <p className="text-[9px] text-gray-600 mt-0.5">{m?.tmax != null ? `${Math.round(m.tmax)}°` : '—'}</p>
                              <p className="text-[9px] text-emerald-600 font-medium mt-0.5">{m?.caN1 != null ? (m.caN1 >= 1000 ? `${(m.caN1/1000).toFixed(1)}k` : `${Math.round(m.caN1)}`) : '—'}</p>
                            </div>
                          )
                        })}
                      </div>
                      <p className="text-[9px] text-gray-400 mt-1">☀️ = météo prévision cette année · chiffre vert = CA N-1 · Soleil → plus de clients · Pluie → moins de clients</p>
                    </div>
                  )}

                  {/* Jours fériés */}
                  {joursFerriesWeek.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">🎌 Jours fériés cette semaine</p>
                      <div className="flex flex-wrap gap-1.5">
                        {joursFerriesWeek.map((h, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                            {h.date.slice(5)} {h.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Vacances scolaires FR — toutes zones */}
                  {vacancesFRWeek.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">🏫 Vacances scolaires France</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(() => {
                          const byName: Record<string, string[]> = {}
                          for (const v of vacancesFRWeek) { if (!byName[v.name]) byName[v.name] = []; byName[v.name].push(v.zone) }
                          return Object.entries(byName).map(([name, zones]) => (
                            <span key={name} className={`text-xs px-2 py-0.5 rounded-full font-medium ${zones.length === 3 ? 'bg-blue-200 text-blue-800' : 'bg-blue-100 text-blue-700'}`}>
                              {name} · Zone{zones.length > 1 ? 's' : ''} {zones.join('+')}
                              {zones.length === 3 && ' 🔥'}
                            </span>
                          ))
                        })()}
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">Zones A+B+C simultanément 🔥 = afflux maximum de touristes français</p>
                    </div>
                  )}

                  {/* Vacances étrangères */}
                  {vacancesEtWeek.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">🌍 Touristes étrangers présents</p>
                      <div className="flex flex-wrap gap-1.5">
                        {[...new Map(vacancesEtWeek.map(v => [v.pays, v])).values()].map((v, i) => (
                          <span key={i} className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">{v.pays}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {joursFerriesWeek.length === 0 && vacancesFRWeek.length === 0 && vacancesEtWeek.length === 0 && (
                    <p className="text-xs text-gray-400 italic">Semaine ordinaire — pas de jour férié ni de vacances détectés</p>
                  )}

                  {/* Événements locaux */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">📝 Événements locaux (fêtes, marchés, tournois...)</p>
                    {/* Liste des événements */}
                    {evtsLocaux.length > 0 && (
                      <div className="space-y-1.5 mb-3">
                        {evtsLocaux.map((evt) => (
                          <div key={evt.id} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5">
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-gray-800">{evt.nom}</span>
                              <span className="text-xs text-gray-400 ml-2">{evt.lieu}</span>
                              <span className="text-xs text-gray-500 ml-2">
                                {String(evt.jour).padStart(2,'0')}/{String(evt.mois).padStart(2,'0')}{evt.recurrent ? ' 🔁' : `/${evt.annee}`}
                              </span>
                            </div>
                            <button onClick={() => updateConfigAndSave('evenements_locaux', evtsLocaux.filter(e => e.id !== evt.id))}
                              className="text-gray-300 hover:text-red-400 flex-shrink-0"><X size={14} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Formulaire d'ajout */}
                    <div className="border border-dashed border-gray-200 rounded-lg p-3 space-y-2 bg-white">
                      <div className="flex gap-2">
                        <input value={newEvt.nom} onChange={e => setNewEvt(v => ({ ...v, nom: e.target.value }))}
                          placeholder="Nom de l'événement" className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                        <input value={newEvt.lieu} onChange={e => setNewEvt(v => ({ ...v, lieu: e.target.value }))}
                          placeholder="Lieu" className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-500">Date :</span>
                        <select value={newEvt.jour} onChange={e => setNewEvt(v => ({ ...v, jour: +e.target.value }))}
                          className="text-sm border border-gray-200 rounded-lg px-2 py-1 outline-none focus:border-emerald-400">
                          {Array.from({length:31},(_,i)=><option key={i+1} value={i+1}>{String(i+1).padStart(2,'0')}</option>)}
                        </select>
                        <select value={newEvt.mois} onChange={e => setNewEvt(v => ({ ...v, mois: +e.target.value }))}
                          className="text-sm border border-gray-200 rounded-lg px-2 py-1 outline-none focus:border-emerald-400">
                          {['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Aoû','Sep','Oct','Nov','Déc'].map((m,i)=>(
                            <option key={i+1} value={i+1}>{m}</option>
                          ))}
                        </select>
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer ml-1">
                          <input type="checkbox" checked={newEvt.recurrent} onChange={e => setNewEvt(v => ({ ...v, recurrent: e.target.checked, annee: e.target.checked ? undefined : new Date().getFullYear() }))} className="rounded" />
                          Récurrent (chaque année)
                        </label>
                        {!newEvt.recurrent && (
                          <input type="number" value={newEvt.annee ?? new Date().getFullYear()}
                            onChange={e => setNewEvt(v => ({ ...v, annee: +e.target.value }))}
                            min={2024} max={2099} className="w-20 text-sm border border-gray-200 rounded-lg px-2 py-1 outline-none focus:border-emerald-400" />
                        )}
                        <button onClick={() => {
                          if (!newEvt.nom.trim()) return
                          const evt: EvenementLocal = { ...newEvt, id: Date.now().toString() }
                          updateConfigAndSave('evenements_locaux', [...evtsLocaux, evt])
                          setNewEvt({ nom: '', lieu: '', jour: 1, mois: 1, recurrent: true })
                        }} className="ml-auto px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-700 font-medium flex items-center gap-1">
                          <Plus size={13} /> Ajouter
                        </button>
                      </div>
                    </div>
                    {evtsLocauxWeek.length > 0 && (
                      <p className="text-xs text-emerald-600 mt-1.5">✓ {evtsLocauxWeek.length} événement(s) actif(s) cette semaine → transmis à l'IA</p>
                    )}
                  </div>

                  <p className="text-xs text-emerald-600">💡 Ces données sont transmises à l'IA pour adapter le nombre de personnel à l'affluence attendue.</p>
                </div>

                {/* Règles Magasin (ouverture + livraison) */}
                <div className="border border-indigo-200 rounded-xl p-4 space-y-4 bg-indigo-50/30">
                  <p className="text-sm font-semibold text-indigo-800">📋 Règles Magasin</p>
                  {/* Horaires d'ouverture */}
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">🕐 Horaires d'ouverture</p>
                    <div className="space-y-1">
                      {rules.openingHours.map((day, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs text-gray-600 w-16 font-medium">{DAY_LABELS_FULL[i]}</span>
                          <label className="flex items-center gap-1 text-xs text-gray-500 w-16">
                            <input type="checkbox" checked={day.closed} onChange={e => updateOpeningDayC(i, { closed: e.target.checked })} className="rounded" />
                            Fermé
                          </label>
                          {!day.closed && (
                            <>
                              <input type="time" value={day.open} onChange={e => updateOpeningDayC(i, { open: e.target.value })} className="text-xs border border-gray-200 rounded px-1.5 py-0.5 outline-none focus:border-indigo-400 w-24 bg-white" />
                              <span className="text-gray-400 text-xs">–</span>
                              <input type="time" value={day.close} onChange={e => updateOpeningDayC(i, { close: e.target.value })} className="text-xs border border-gray-200 rounded px-1.5 py-0.5 outline-none focus:border-indigo-400 w-24 bg-white" />
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Jours de livraison */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-gray-600">🚚 Jours de livraison</p>
                      <button onClick={addDeliveryPeriodC} className="flex items-center gap-1 text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 font-medium">
                        <Plus size={11} /> Période
                      </button>
                    </div>
                    <div className="space-y-2">
                      {rules.deliveryPeriods.map(period => (
                        <div key={period.id} className="border border-gray-200 rounded-lg p-2 space-y-1.5 bg-white">
                          <div className="flex items-center gap-2">
                            <input value={period.label} onChange={e => updateDeliveryPeriodC(period.id, { label: e.target.value })} className="flex-1 text-xs font-medium border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400" />
                            <button onClick={() => removeDeliveryPeriodC(period.id)} className="text-gray-300 hover:text-red-400"><Trash2 size={13} /></button>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-600 flex-wrap">
                            <span className="text-gray-400">Du</span>
                            <select value={period.startDay} onChange={e => updateDeliveryPeriodC(period.id, { startDay: +e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none">
                              {period.startDay === 0 && <option value={0}>—</option>}
                              {Array.from({length:31},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                            </select>
                            <select value={period.startMonth} onChange={e => updateDeliveryPeriodC(period.id, { startMonth: +e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none">
                              {period.startMonth === 0 && <option value={0}>—</option>}
                              {MONTHS_R.map((m,mi)=><option key={mi+1} value={mi+1}>{m}</option>)}
                            </select>
                            <span className="text-gray-400">au</span>
                            <select value={period.endDay} onChange={e => updateDeliveryPeriodC(period.id, { endDay: +e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none">
                              {period.endDay === 0 && <option value={0}>—</option>}
                              {Array.from({length:31},(_,i)=><option key={i+1} value={i+1}>{i+1}</option>)}
                            </select>
                            <select value={period.endMonth} onChange={e => updateDeliveryPeriodC(period.id, { endMonth: +e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 text-xs outline-none">
                              {period.endMonth === 0 && <option value={0}>—</option>}
                              {MONTHS_R.map((m,mi)=><option key={mi+1} value={mi+1}>{m}</option>)}
                            </select>
                          </div>
                          <div className="flex gap-1 flex-wrap">
                            {DAY_SHORT_R.map((d, di) => (
                              <button key={di} onClick={() => toggleDeliveryDayC(period.id, di)} className={`w-7 h-7 text-xs font-medium rounded-full transition-colors ${period.days.includes(di) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>{d}</button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-indigo-600">💡 Ces règles sont transmises à l'IA lors de la génération. Sauvegarder avec 💾 ci-dessous.</p>
                </div>

                {/* Saison */}
                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-gray-800">🌞 Saison haute</p>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-10">Du</span>
                    <input type="text" placeholder="06-01" value={planningConfig.saison_haute_debut}
                      onChange={e => updateConfig('saison_haute_debut', e.target.value)}
                      className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-center outline-none focus:border-purple-400" />
                    <span className="text-sm text-gray-600">au</span>
                    <input type="text" placeholder="08-31" value={planningConfig.saison_haute_fin}
                      onChange={e => updateConfig('saison_haute_fin', e.target.value)}
                      className="w-24 text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-center outline-none focus:border-purple-400" />
                    <span className="text-xs text-gray-400">(MM-JJ)</span>
                  </div>
                </div>

                {/* Livraison basse saison */}
                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-gray-800">🚚 Livraison — Basse saison</p>
                  <DeliveryDays label="Jours de livraison" cfgKey="livraison_basse_jours" />
                  <div className="grid grid-cols-2 gap-3">
                    <NumInput label="Minimum staff" cfgKey="livraison_basse_min" min={1} max={20} />
                    <NumInput label="Idéal staff" cfgKey="livraison_basse_ideal" min={1} max={20} />
                  </div>
                </div>

                {/* Livraison haute saison */}
                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-gray-800">🚚 Livraison — Haute saison</p>
                  <DeliveryDays label="Jours de livraison" cfgKey="livraison_haute_jours" />
                  <div className="grid grid-cols-2 gap-3">
                    <NumInput label="Minimum staff" cfgKey="livraison_haute_min" min={1} max={20} />
                    <NumInput label="Idéal staff" cfgKey="livraison_haute_ideal" min={1} max={20} />
                  </div>
                </div>

                {/* Réglementation */}
                <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-gray-800">⚖️ Réglementation</p>
                  <NumInput label="Repos minimum entre shifts (h)" cfgKey="repos_min_heures" min={0} max={24} />
                  <NumInput label="Amplitude max journalière (h)" cfgKey="amplitude_max_heures" min={4} max={24} />
                  <NumInput label="Tolérance heures contrat (h)" cfgKey="tolerance_heures" min={0} max={2} step={0.25} />
                  <ToggleCfg label="Pause 20 min incluse dans le temps de travail" desc="Convention collective : les heures sont comptées en brut (amplitude)" cfgKey="pause_incluse" />
                </div>

                {/* Contraintes humaines */}
                <div className="border border-gray-200 rounded-xl p-4 space-y-4">
                  <p className="text-sm font-semibold text-gray-800">👥 Contraintes humaines</p>
                  <ToggleCfg label="Resp prioritaire sur non-Resp" desc="Ex: Resp Frais prioritaire sur Frais si dispo" cfgKey="resp_prioritaire" />
                  <ToggleCfg label="Saisonnier toujours avec un CDI/CDD" desc="Un employé saisonnier ne peut pas être seul sur une plage horaire" cfgKey="saisonnier_cdi_required" />

                  {/* Binômes */}
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">Binômes (toujours planifiés ensemble)</p>
                    <div className="space-y-1.5 mb-2">
                      {(planningConfig.binomes || []).map((binome: string[], bi: number) => (
                        <div key={bi} className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-1.5">
                          <span className="flex-1 text-sm text-blue-800">{binome.join(' + ')}</span>
                          <button onClick={() => updateConfigAndSave('binomes', (planningConfig.binomes || []).filter((_: any, i: number) => i !== bi))}
                            className="text-gray-300 hover:text-red-400"><X size={14} /></button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <select value={binomeEmp1} onChange={e => setBinomeEmp1(e.target.value)}
                        className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-purple-400">
                        <option value="">Employé 1</option>
                        {employees.filter(e => e.firstName !== binomeEmp2).map(e => (
                          <option key={e.id} value={e.firstName}>{e.firstName} {e.lastName}</option>
                        ))}
                      </select>
                      <select value={binomeEmp2} onChange={e => setBinomeEmp2(e.target.value)}
                        className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-purple-400">
                        <option value="">Employé 2</option>
                        {employees.filter(e => e.firstName !== binomeEmp1).map(e => (
                          <option key={e.id} value={e.firstName}>{e.firstName} {e.lastName}</option>
                        ))}
                      </select>
                      <button onClick={() => {
                        if (binomeEmp1 && binomeEmp2 && binomeEmp1 !== binomeEmp2) {
                          updateConfigAndSave('binomes', [...(planningConfig.binomes || []), [binomeEmp1, binomeEmp2]])
                          setBinomeEmp1(''); setBinomeEmp2('')
                        }
                      }} className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 font-medium">
                        <Plus size={13} />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Toujours sur la même plage horaire (matin ou après-midi)</p>
                  </div>

                  {/* Groupes liés */}
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">Groupes liés (même shift obligatoire)</p>
                    <div className="space-y-1.5 mb-2">
                      {(planningConfig.groupes_lies || []).map((groupe, gi) => (
                        <div key={gi} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                          <span className="flex-1 text-sm text-gray-700">{groupe.join(' + ')}</span>
                          <button onClick={() => updateConfig('groupes_lies', (planningConfig.groupes_lies || []).filter((_: any, i: number) => i !== gi))}
                            className="text-gray-300 hover:text-red-400"><X size={14} /></button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input value={newGroupInput} onChange={e => setNewGroupInput(e.target.value)}
                        placeholder="Prénom1, Prénom2"
                        className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-purple-400" />
                      <button onClick={() => {
                        const noms = newGroupInput.split(',').map(s => s.trim()).filter(Boolean)
                        if (noms.length >= 2) { updateConfig('groupes_lies', [...(planningConfig.groupes_lies || []), noms]); setNewGroupInput('') }
                      }} className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 font-medium">
                        <Plus size={13} />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Ex: "Gilbert, Petchara" → toujours le même créneau</p>
                  </div>
                </div>

                {/* Règles opérationnelles */}
                <div className="border border-emerald-200 bg-emerald-50/30 rounded-xl p-4 space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-emerald-900">⚙️ Règles opérationnelles</p>
                    <p className="text-xs text-gray-500 mt-0.5">Tous les seuils et horaires utilisés dans le prompt de l'IA. Modifie-les si le comportement ne correspond pas à ton magasin.</p>
                  </div>

                  {/* Couverture */}
                  <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-3">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Couverture des créneaux</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Min. employés/créneau</label>
                        <input type="number" min={1} max={10} value={(planningConfig as any).min_employes_creneau ?? 2}
                          onChange={e => updateConfig('min_employes_creneau', parseInt(e.target.value) || 2)}
                          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                        <p className="text-[10px] text-gray-400 mt-0.5">Défaut : 2</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Écart max matin/AM</label>
                        <input type="number" min={0} max={5} value={(planningConfig as any).ecart_max_matin_aprem ?? 1}
                          onChange={e => updateConfig('ecart_max_matin_aprem', parseInt(e.target.value) || 0)}
                          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                        <p className="text-[10px] text-gray-400 mt-0.5">Ex : 1 = 3 matin max si 2 AM</p>
                      </div>
                    </div>
                  </div>

                  {/* Horaires magasin */}
                  <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-3">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Horaires magasin</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Arrivée préparation (avant ouverture)</label>
                        <input type="time" value={(planningConfig as any).heure_arrivee_preparation ?? '07:00'}
                          onChange={e => updateConfig('heure_arrivee_preparation', e.target.value)}
                          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Départ fermeture (après clôture)</label>
                        <input type="time" value={(planningConfig as any).heure_depart_fermeture ?? '20:15'}
                          onChange={e => updateConfig('heure_depart_fermeture', e.target.value)}
                          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                      </div>
                    </div>
                  </div>

                  {/* Livraisons */}
                  <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-3">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Livraisons</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Heure début livraison</label>
                        <input type="time" value={(planningConfig as any).heure_livraison_matin ?? '06:00'}
                          onChange={e => updateConfig('heure_livraison_matin', e.target.value)}
                          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Compétence livraison</label>
                        <input type="text" value={(planningConfig as any).competence_livraison ?? 'Réception livraison'}
                          onChange={e => updateConfig('competence_livraison', e.target.value)}
                          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                        <p className="text-[10px] text-gray-400 mt-0.5">Nom exact de la compétence dans les profils</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Basse saison min (palettes)</label>
                        <input type="number" min={1} max={30} value={(planningConfig as any).livraison_basse_min_palettes ?? 8}
                          onChange={e => updateConfig('livraison_basse_min_palettes', parseInt(e.target.value) || 8)}
                          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Basse saison max (palettes)</label>
                        <input type="number" min={1} max={30} value={(planningConfig as any).livraison_basse_max_palettes ?? 10}
                          onChange={e => updateConfig('livraison_basse_max_palettes', parseInt(e.target.value) || 10)}
                          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Haute saison max (palettes)</label>
                        <input type="number" min={1} max={50} value={(planningConfig as any).livraison_haute_max_palettes ?? 15}
                          onChange={e => updateConfig('livraison_haute_max_palettes', parseInt(e.target.value) || 15)}
                          className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                      </div>
                    </div>
                  </div>

                  {/* Saisonniers */}
                  <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-3">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Saisonniers</p>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">CDI/CDD min. par créneau avec saisonnier</label>
                      <input type="number" min={0} max={5} value={(planningConfig as any).min_cdi_par_saisonnier ?? 1}
                        onChange={e => updateConfig('min_cdi_par_saisonnier', parseInt(e.target.value) || 1)}
                        className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-emerald-400" />
                      <p className="text-[10px] text-gray-400 mt-0.5">0 = désactiver la règle, 1 = toujours 1 CDI/CDD si saisonnier planifié</p>
                    </div>
                  </div>
                </div>

                {/* Contexte touristique */}
                <div className="border border-sky-200 bg-sky-50/30 rounded-xl p-4 space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-sky-900">🌍 Contexte touristique</p>
                    <p className="text-xs text-gray-500 mt-0.5">Données transmises à l'IA pour adapter le personnel aux flux de visiteurs.</p>
                  </div>

                  {/* Zones scolaires FR */}
                  <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Vacances scolaires françaises</p>
                    <div className="flex gap-2">
                      {['A','B','C'].map(z => (
                        <div key={z} className="flex-1 text-center py-1.5 rounded-lg bg-blue-100 text-blue-800 text-xs font-semibold border border-blue-200">
                          Zone {z} ✓
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-400">Les 3 zones sont toujours toutes trackées — l'IA sait quand plusieurs zones coïncident (afflux maximum).</p>
                  </div>

                  {/* Pays étrangers */}
                  <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Pays étrangers à surveiller</p>
                    <p className="text-[10px] text-gray-400 mb-1">Activer les nationalités présentes dans ta zone. L'IA renforce le personnel quand ces pays sont en vacances.</p>
                    <div className="flex flex-wrap gap-2">
                      {['🇬🇧 Royaume-Uni','🇳🇱 Pays-Bas','🇩🇪 Allemagne','🇧🇪 Belgique','🇨🇭 Suisse','🇪🇸 Espagne','🇮🇹 Italie','🇸🇪 Scandinavie','🇺🇸 Amérique du Nord'].map(pays => {
                        const actifs: string[] = (planningConfig as any).pays_etrangers_actifs ?? []
                        const on = actifs.includes(pays)
                        return (
                          <button key={pays} onClick={() => {
                            const cur: string[] = (planningConfig as any).pays_etrangers_actifs ?? []
                            updateConfig('pays_etrangers_actifs', on ? cur.filter(p => p !== pays) : [...cur, pays])
                          }} className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${on ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-400 border-gray-200 hover:border-sky-400'}`}>
                            {pays}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Événements récurrents (hardcodés) */}
                  <div className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Événements récurrents auto-détectés</p>
                    <p className="text-[10px] text-gray-400 mb-1">Détectés automatiquement par date chaque année. Ajoute tes propres événements dans l'onglet "Contexte semaine".</p>
                    <div className="space-y-1">
                      {[
                        { date: '21 juin', label: 'Fête de la Musique', note: '+affluence' },
                        { date: '14 juillet', label: 'Fête Nationale', note: 'barbecues, pique-niques (+CA)' },
                        { date: '15 août', label: 'Assomption', note: 'forte affluence touristes' },
                        { date: '24 déc.', label: 'Veille de Noël', note: 'pics achats dernière minute' },
                        { date: '31 déc.', label: 'Réveillon', note: 'courses de fête' },
                        { date: '1er w-e sept.', label: 'Fête des Vendanges de Cassis', note: 'Cassis uniquement' },
                        { date: '15 juillet', label: 'Festival de Cassis', note: 'Cassis — haute affluence' },
                      ].map((e, i) => (
                        <div key={i} className="flex items-center justify-between text-xs px-2 py-1 bg-gray-50 rounded-lg">
                          <span className="text-gray-500 w-24 flex-shrink-0">{e.date}</span>
                          <span className="text-gray-700 font-medium flex-1">{e.label}</span>
                          <span className="text-gray-400 text-[10px]">{e.note}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Priorités IA */}
                <div className="border border-indigo-200 bg-indigo-50/30 rounded-xl p-4 space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-indigo-900">🎯 Priorités de l'IA</p>
                    <p className="text-xs text-gray-500 mt-0.5">Le % indique l'importance <strong>relative</strong> entre critères. 80% + 40% = le premier est 2× plus prioritaire.</p>
                  </div>

                  <div className="space-y-3">
                    {([
                      { key: 'poids_sous_couverture', max: 20000, label: 'Couverture minimale',      desc: 'Min 2 personnes/créneau tous les jours',                color: 'red' },
                      { key: 'poids_ecart_contrat',   max: 10000, label: 'Heures contractuelles',    desc: 'Total hebdo exact pour chaque employé',                 color: 'orange' },
                      { key: 'poids_livraison',       max: 5000,  label: 'Jours de livraison',       desc: 'Effectif renforcé dès 06h00',                           color: 'indigo' },
                      { key: 'poids_priorite_poste',  max: 2000,  label: 'Hiérarchie des postes',    desc: 'Resp. FL et Frais en priorité sur leur rayon',          color: 'purple' },
                      { key: 'poids_competence',      max: 1000,  label: 'Compétences par créneau',  desc: 'Bonnes compétences aux bons horaires',                  color: 'blue' },
                      { key: 'poids_equite',          max: 400,   label: 'Équité de charge',         desc: 'Répartition équilibrée entre employés',                 color: 'teal' },
                      { key: 'poids_stabilite',       max: 200,   label: 'Stabilité des horaires',   desc: 'Cohérence des horaires sur la semaine',                 color: 'gray' },
                      { key: 'poids_jours_feries',    max: 100,   label: 'Jours fériés',             desc: '0% = ignoré · 100% = renforcer fortement le personnel', color: 'yellow' },
                      { key: 'poids_vacances',        max: 100,   label: 'Vacances & touristes',     desc: 'Vacances scolaires + afflux de touristes étrangers',     color: 'yellow' },
                      { key: 'poids_evenements',      max: 100,   label: 'Événements locaux',        desc: 'Fêtes, marchés, concerts… saisis dans la config',       color: 'yellow' },
                      { key: 'poids_meteo',           max: 100,   label: 'Météo',                    desc: 'Soleil/chaleur = +personnel, pluie = standard',          color: 'sky' },
                      { key: 'poids_ca_n1',           max: 100,   label: 'CA N-1',                   desc: "Chiffre d'affaires du même jour l'an passé",             color: 'sky' },
                    ] as { key: string; max: number; label: string; desc: string; color: string }[]).map(({ key, max, label, desc, color }) => {
                      const val = (planningConfig as any)[key] ?? Math.round(max * 0.5)
                      const pct = Math.min(100, Math.max(0, Math.round((val / max) * 100)))
                      const barColor: Record<string, string> = {
                        red: 'accent-red-600', orange: 'accent-orange-500', indigo: 'accent-indigo-600',
                        purple: 'accent-purple-600', blue: 'accent-blue-500', teal: 'accent-teal-500',
                        gray: 'accent-gray-400', yellow: 'accent-yellow-500', sky: 'accent-sky-500'
                      }
                      const textColor: Record<string, string> = {
                        red: 'text-red-600', orange: 'text-orange-500', indigo: 'text-indigo-700',
                        purple: 'text-purple-700', blue: 'text-blue-600', teal: 'text-teal-600',
                        gray: 'text-gray-500', yellow: 'text-yellow-600', sky: 'text-sky-600'
                      }
                      return (
                        <div key={key} className="bg-white border border-gray-200 rounded-xl px-3 py-3 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-sm font-semibold text-gray-800">{label}</span>
                              <p className="text-xs text-gray-400">{desc}</p>
                            </div>
                            <span className={`text-lg font-bold tabular-nums ${textColor[color]}`}>{pct}%</span>
                          </div>
                          <input type="range" min={0} max={100} value={pct}
                            onChange={e => updateConfig(key, Math.round((parseInt(e.target.value) / 100) * max))}
                            className={`w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-200 ${barColor[color]}`} />
                          <div className="flex justify-between text-[10px] text-gray-300">
                            <span>Ignoré</span><span>Faible</span><span>Normal</span><span>Important</span><span>Critique</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Sauvegarder */}
                <button onClick={() => { saveConfigToBackend(); saveConfig() }} disabled={configSaving}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors ${configSaved ? 'bg-green-100 text-green-700' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                  {configSaving ? 'Sauvegarde...' : configSaved ? '✓ Paramètres sauvegardés' : '💾 Sauvegarder tous les paramètres IA'}
                </button>
              </div>
            )
          })()}

          {/* Result */}
          {result && (
            <div className={`mt-5 p-4 rounded-xl border ${result.error ? 'bg-red-50 border-red-200' : 'bg-purple-50 border-purple-200'}`}>
              {result.error ? (
                <>
                  <p className="text-sm font-semibold text-red-700 mb-1">❌ Erreur UtilIA</p>
                  <p className="text-sm text-red-600">{result.error}</p>
                </>
              ) : (
              <>
              <p className="text-sm font-semibold text-purple-800 mb-2">Résultat</p>
              <p className="text-sm text-purple-700 mb-2">{result.created} shift{result.created !== 1 ? 's' : ''} créé{result.created !== 1 ? 's' : ''}</p>
              {result.hoursReport?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-700 mb-1.5">Heures planifiées vs contrat :</p>
                  <div className="space-y-1">
                    {result.hoursReport.map((r: any, i: number) => (
                      <div key={i} className={`flex items-center justify-between text-xs px-2 py-1 rounded ${r.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                        <span>{r.name}</span>
                        <span className="font-mono font-medium">{r.scheduled}h / {r.contract}h {r.ok ? '✓' : '⚠'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {result.unfilledSlots?.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-medium text-amber-700 mb-1">Créneaux non couverts :</p>
                  {result.unfilledSlots.map((s: any, i: number) => (
                    <p key={i} className="text-xs text-amber-600">• {SP_DAYS[s.dayOfWeek]} {s.startTime}–{s.endTime} : {s.assigned}/{s.needed} employés</p>
                  ))}
                </div>
              )}
              {result.warnings?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-orange-700 mb-1">Alertes :</p>
                  {result.warnings.map((w: string, i: number) => (
                    <p key={i} className="text-xs text-orange-600">• {w}</p>
                  ))}
                </div>
              )}
              </>
              )}
            </div>
          )}
        </div>

        {/* Résultat generation — affiché dans le footer (toujours visible) */}
        {result && (
          <div className={`px-6 py-3 border-t flex-shrink-0 text-sm ${result.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            {result.error ? (
              <p className="text-red-700 font-medium">❌ {result.error}</p>
            ) : result.weeks ? (
              // Résultat cycle 5 semaines
              <div>
                <p className="text-green-700 font-semibold mb-1">✅ Cycle 5 semaines — {result.totalCreated} shifts créés en brouillon</p>
                <div className="grid grid-cols-5 gap-1">
                  {result.weeks.map((w: any) => (
                    <div key={w.week} className="text-center bg-white rounded-lg px-2 py-1.5 border border-green-200">
                      <p className="text-xs font-bold text-green-800">Sem {w.week}</p>
                      <p className="text-xs text-green-700">{w.created} shifts</p>
                      {w.unfilledSlots?.length > 0 && <p className="text-[10px] text-orange-500">⚠️ {w.unfilledSlots.length} créneaux</p>}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-green-700 font-medium">✅ {result.created} shift{result.created !== 1 ? 's' : ''} créé{result.created !== 1 ? 's' : ''} en brouillon — clique Publier pour les valider</p>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-col gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl flex-shrink-0">
          {/* Ligne cycle 5 semaines */}
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Cycle 5 semaines :</span>
            <input type="date" value={cycleStartDate} onChange={e => setCycleStartDate(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-purple-400 bg-white" />
            <button onClick={generateCycle} disabled={cycleGenerating || generating || buildSlots().length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition-colors">
              {cycleGenerating ? 'Génération...' : <><Wand2 size={12} /> Générer 5 semaines</>}
            </button>
            <span className="text-xs text-gray-400">Répartit Sam/Dim équitablement sur le cycle</span>
          </div>
          {/* Ligne boutons principaux — Règle n°4 (04/07) : 2 chemins de génération
              (cycle + semaine), tous deux sur le moteur déterministe + couche IA CA N-1.
              Les variantes LLM (Gemma/Gemini/Claude) sont retirées de l'UI. */}
          <div className="flex items-center justify-between">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            {result ? 'Fermer' : 'Annuler'}
          </button>
          <div className="flex items-center gap-3">
            <button onClick={generate} disabled={generating || cycleGenerating || buildSlots().length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white text-sm font-medium rounded-lg transition-colors">
              {generating ? 'Génération...' : <><Wand2 size={14} /> Générer la semaine</>}
            </button>
          </div>
          </div>
        </div>

        {/* Résultat IA : résumé */}
        {result?.summary && (
          <div className="mx-6 mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
            <p className="font-semibold mb-1">✦ Analyse UtilIA</p>
            <p className="leading-relaxed">{result.summary}</p>
            {result.warnings?.length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {result.warnings.map((w: string, i: number) => (
                  <li key={i} className="text-amber-700 text-xs">⚠ {w}</li>
                ))}
              </ul>
            )}
            {result.created != null && (
              <p className="mt-2 text-xs text-amber-600 font-medium">{result.created} shift{result.created !== 1 ? 's' : ''} créé{result.created !== 1 ? 's' : ''} en brouillon.</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Import CA N-1 ──────────────────────────────────────────────────────────
function RevenueImportModal({ establishmentId, onClose }: { establishmentId: string; onClose: () => void }) {
  const [csv, setCsv] = useState('')
  const [preview, setPreview] = useState<{ date: string; revenue: number }[]>([])
  const [importing, setImporting] = useState(false)
  const [done, setDone] = useState<number | null>(null)
  const [error, setError] = useState('')

  function parseCsv(text: string) {
    const lines = text.trim().split('\n').filter(l => l.trim())
    const rows: { date: string; revenue: number }[] = []
    for (const line of lines) {
      const parts = line.split(/[,;]/).map(p => p.trim().replace(/"/g, ''))
      if (parts.length < 2) continue
      const date = parts[0]
      const rev = parseFloat(parts[1].replace(',', '.'))
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || isNaN(rev)) continue
      rows.push({ date, revenue: rev })
    }
    return rows
  }

  function handleChange(text: string) {
    setCsv(text)
    setPreview(parseCsv(text).slice(0, 5))
    setError('')
    setDone(null)
  }

  async function doImport() {
    const rows = parseCsv(csv)
    if (rows.length === 0) { setError('Aucune ligne valide trouvée.'); return }
    setImporting(true)
    try {
      const res = await api.post('/revenue/bulk', { establishmentId, rows })
      setDone(res.data.upserted)
    } catch {
      setError('Erreur lors de l\'import.')
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} className="text-emerald-600" />
            <h2 className="font-bold text-gray-900">Importer CA N-1</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Collez votre CSV (format <code className="bg-gray-100 px-1 rounded">date,chiffre_d_affaires</code> — séparateur virgule ou point-virgule).
          </p>
          <p className="text-xs text-gray-400">Exemple :<br />
            <code className="bg-gray-50 block mt-1 p-2 rounded border text-xs">2025-03-18,2450<br />2025-03-19,3100.50<br />2025-03-20,0</code>
          </p>
          <textarea
            rows={8}
            value={csv}
            onChange={e => handleChange(e.target.value)}
            placeholder="2025-03-18,2450&#10;2025-03-19,3100&#10;..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
          />
          {preview.length > 0 && (
            <div className="text-xs text-gray-500">
              <span className="font-medium">{parseCsv(csv).length} ligne{parseCsv(csv).length > 1 ? 's' : ''} détectée{parseCsv(csv).length > 1 ? 's' : ''}</span>
              {preview.length > 0 && <span className="text-gray-400"> — Aperçu : {preview.map(r => `${r.date} → ${r.revenue}€`).join(', ')}{parseCsv(csv).length > 5 ? '...' : ''}</span>}
            </div>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {done != null && <p className="text-sm text-emerald-600 font-medium">{done} enregistrement{done > 1 ? 's' : ''} importé{done > 1 ? 's' : ''}.</p>}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            {done != null ? 'Fermer' : 'Annuler'}
          </button>
          {done == null && (
            <button onClick={doImport} disabled={importing || !csv.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white text-sm font-medium rounded-lg transition-colors">
              <Upload size={14} /> {importing ? 'Import...' : 'Importer'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
